import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useCallback
} from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
// import { Divider } from '@material-ui/core';
import InfoField from './InfoField.js';
import PhoneField from './PhoneField.js';
import OpeningDateField from './OpeningDateField.js';
import Categories from './Categories.js';
import Hours from './Hours.js';
import ServiceArea from './ServiceArea.js';
import ProfileOptimizationRating from './ProfileOptimizationRating.js';
import { getGoogleProfileInfo } from './services/index';
import { selectedClientSelector } from '../../store/slices/selectedClient';
import {
  API_UPDATE_GOOGLE_PROFILE_INFO,
  API_GET_CATEGORIES,
  API_ENDPOINT_QUALITY_AGENT,
  API_ENDPOINT_FIX_FIELD,
  API_ENDPOINT_DISMISS_FIELD
} from '../../../src/utils';
import SpecialHours from './SpecialHours';
import './styles.css';
import QualityAgentNotification from './QualityAgentNotification';

// SUGERENCIAS MOCKEADAS ALEATORIAS - Movido fuera del componente para evitar recreación
const agentSuggestions = {
  serviceArea: [
    'Add more cities to expand your coverage.',
    'Include nearby metropolitan areas.',
    'Check that all service areas are correctly spelled.'
  ],
  reviewFrequency: [
    'Ask your clients to leave a review after each service.',
    'Send periodic reminders to get more reviews.'
  ],
  googleRating: [
    'Ask satisfied clients to leave a positive review.',
    'Respond to negative reviews to improve your reputation.'
  ],
  numberOfQAs: [
    'Add relevant FAQs for your clients.',
    'Answer the most common questions on your profile.'
  ],
  answeredAllQAs: [
    'Answer all questions to build trust.',
    'Make sure there are no unanswered questions.'
  ],
  reviewReplies: [
    'Reply to all reviews to show engagement.',
    'Thank your clients for their feedback.'
  ],
  numberOfImages: [
    'Add more high-quality images to attract clients.',
    'Include photos of your products and team.'
  ],
  postFrequency: [
    'Post at least once a week.',
    'Maintain a consistent posting schedule.'
  ],
  postKeywordOptimization: [
    'Include relevant keywords in your posts.',
    'Optimize titles and descriptions with popular terms.'
  ]
};

const GoogleBusinessProfile = forwardRef(
  (
    {
      isEditing,
      setIsEditing,
      showQualityAgent,
      setShowQualityAgent,
      handleShowQualityAgentRef,
      initialOptimizationPercentage = 0
    },
    ref
  ) => {
    // TODOS LOS HOOKS DEBEN ESTAR AL PRINCIPIO
    const [googleProfileInfo, setGoogleProfileInfo] = useState({
      loading: true,
      data: {}
    });
    const [currSection, setCurrSection] = useState('about');
    const [editedFields, setEditedFields] = useState({});
    const [categories, setCategories] = useState([]);
    const [gettingCategories, setGettingCategories] = useState(false);
    const [hours, setHours] = useState([]);
    const [fixedDescription, setFixedDescription] = useState(null);
    const [loadingAgent, setLoadingAgent] = useState(false);
    const [qualityAgentData, setQualityAgentData] = useState(null);
    const [randomAgentSuggestions, setRandomAgentSuggestions] = useState({});
    const [fixedServiceArea, setFixedServiceArea] = useState(null);
    const [forceRender, setForceRender] = useState(0);
    const [addedRecommendedServices, setAddedRecommendedServices] = useState(
      new Set()
    );
    const [addedRecommendedServiceAreas, setAddedRecommendedServiceAreas] =
      useState(new Set());
    const [fixedServicesList, setFixedServicesList] = useState(null);
    // Estados para campos corregidos por el quality agent
    const [fixedPostsKeywords, setFixedPostsKeywords] = useState(null);
    const [recommendedServices, setRecommendedServices] = useState(null);
    const [recommendedCategories, setRecommendedCategories] = useState(null);
    const [recommendedServiceAreas, setRecommendedServiceAreas] =
      useState(null);

    const [loadingFix, setLoadingFix] = useState({});
    const [loadingDismiss, setLoadingDismiss] = useState({});

    const [dismissedNotifications, setDismissedNotifications] = useState({});

    // Función helper para verificar si hay alguna operación en progreso
    const isAnyOperationInProgress = useCallback(() => {
      return (
        Object.values(loadingFix).some((loading) => loading === true) ||
        Object.values(loadingDismiss).some((loading) => loading === true)
      );
    }, [loadingFix, loadingDismiss]);

    const [saveMessage, setSaveMessage] = useState({
      show: false,
      type: '',
      message: ''
    });

    const { selectedClient } = useSelector(selectedClientSelector);

    useEffect(() => {
      console.log('Dismissed notifications:', dismissedNotifications);
    }, [dismissedNotifications]);

    // Definir getCategories antes de usarla en useEffect
    const getCategories = useCallback(async () => {
      setGettingCategories(true);
      const equalsCheck = (a, b) => {
        return JSON.stringify(a) === JSON.stringify(b);
      };
      try {
        await axios.get(API_GET_CATEGORIES).then((response) => {
          const { categories: cs } = response.data;
          if (
            !equalsCheck(
              categories,
              cs.map((category) => ({
                ...category,
                value: category.id,
                label: category.display_name
              }))
            ) ||
            googleProfileInfo.data?.categories?.primaryCategory?.displayName ===
              ''
          ) {
            setCategories(
              cs.map((category) => ({
                ...category,
                value: category.id,
                label: category.display_name
              }))
            );
          }
          setGettingCategories(false);
        });
      } catch (e) {
        console.error(
          'An error has ocurred retrieving the google categories',
          e
        );
        setGettingCategories(false);
        return [];
      }
      setGettingCategories(false);
    }, [
      categories,
      googleProfileInfo.data?.categories?.primaryCategory?.displayName
    ]);

    // useEffect hooks
    useEffect(() => {
      if (categories.length === 0 && !gettingCategories) {
        getCategories();
      }
    }, [categories.length, gettingCategories, getCategories]);

    useEffect(() => {
      if (googleProfileInfo.data?.regularHours?.periods) {
        const completeHours = (periods) => {
          const defaultOpenTime = { hours: 8 };
          const defaultCloseTime = { hours: 17 };
          const defaultDayOrder = [
            'MONDAY',
            'TUESDAY',
            'WEDNESDAY',
            'THURSDAY',
            'FRIDAY',
            'SATURDAY',
            'SUNDAY'
          ];

          return periods.map((period, index) => {
            return (
              period || {
                openDay: defaultDayOrder[index],
                openTime: defaultOpenTime,
                closeDay: defaultDayOrder[index],
                closeTime: defaultCloseTime
              }
            );
          });
        };

        const completedHours = completeHours(
          googleProfileInfo.data.regularHours.periods
        );

        if (JSON.stringify(completedHours) !== JSON.stringify(hours)) {
          setHours(completedHours);
        }
      }
    }, [googleProfileInfo.data?.regularHours?.periods, hours]);

    // Definir loadGoogleProfile antes de usarla en useEffect
    const loadGoogleProfile = useCallback(async () => {
      setGoogleProfileInfo({ loading: true, data: null });
      if (selectedClient?.id) {
        try {
          const profileInfo = await getGoogleProfileInfo(selectedClient.id);
          setGoogleProfileInfo(profileInfo);
        } catch (error) {
          console.error('Error loading Google profile info:', error);
          setGoogleProfileInfo({ loading: false, data: null });
        }
      } else {
        setGoogleProfileInfo({ loading: false, data: null });
      }
    }, [selectedClient?.id]);

    useEffect(() => {
      const card = document.getElementById('scrollCard');
      if (card) {
        const handleScroll = () => {
          const sections = card.querySelectorAll('div[id]');
          let newSectionId = '';

          sections.forEach((section) => {
            const rect = section.getBoundingClientRect();
            if (rect.top >= 0 && rect.top <= card.clientHeight) {
              newSectionId = section.id;
            }
          });

          if (newSectionId && newSectionId !== currSection) {
            setCurrSection(newSectionId);
          }
        };

        card.addEventListener('scroll', handleScroll);
        return () => {
          card.removeEventListener('scroll', handleScroll);
        };
      }
    }, [currSection]);

    useEffect(() => {
      loadGoogleProfile();

      return () => {
        setGoogleProfileInfo({}); // Limpiar estado si es necesario
      };
    }, [selectedClient?.id, loadGoogleProfile]);

    // Reiniciar estado de notificaciones cuando cambie el cliente
    useEffect(() => {
      setDismissedNotifications({});
      setShowQualityAgent(false);
      setQualityAgentData(null);
      setFixedDescription(null);
      setFixedServiceArea(null);
      setForceRender(0);
      setFixedServicesList(null);
      // Limpiar estados de campos corregidos
      setFixedPostsKeywords(null);
      setRecommendedServices(null);
      setRecommendedServiceAreas(null);
      setAddedRecommendedServiceAreas(new Set());
      setRandomAgentSuggestions({});
    }, [selectedClient?.id]);

    // useEffect para monitorear cambios en fixedServiceArea
    // useEffect(() => {
    //   console.log('fixedServiceArea changed:', fixedServiceArea);
    //   if (fixedServiceArea !== null) {
    //     setForceRender((prev) => prev + 1);
    //   }
    // }, [fixedServiceArea]);

    // useEffect para procesar servicios recomendados cuando qualityAgentData cambie
    useEffect(() => {
      if (
        qualityAgentData?.recomendations?.serviceItems?.recommendedServices
          ?.message
      ) {
        try {
          const recommendedServicesList = parseRecommendedServices(
            qualityAgentData.recomendations.serviceItems.recommendedServices
              .message
          );
          setRecommendedServices(recommendedServicesList);
        } catch (error) {
          console.error('Error parsing recommended services:', error);
        }
      }

      // Procesar categorías recomendadas
      if (
        qualityAgentData?.recomendations?.categories?.additionalCategories
          ?.message
      ) {
        try {
          const recommendedCategoriesList = JSON.parse(
            qualityAgentData.recomendations.categories.additionalCategories
              .message
          );
          setRecommendedCategories(recommendedCategoriesList);
        } catch (error) {
          console.error('Error parsing recommended categories:', error);
        }
      }

      // Procesar service areas recomendadas
      if (
        qualityAgentData?.recomendations?.serviceArea?.places?.placeInfos ||
        qualityAgentData?.recomendations?.serviceArea?.places?.message
      ) {
        try {
          const apiPlaceInfos =
            qualityAgentData.recomendations?.serviceArea?.places?.placeInfos;
          const messageText =
            qualityAgentData.recomendations?.serviceArea?.places?.message;

          let parsedServiceAreas = [];
          if (apiPlaceInfos && apiPlaceInfos.length > 0) {
            parsedServiceAreas = apiPlaceInfos.map((place, index) => ({
              id: `place_${index}`,
              placeName: place.placeName || place.name,
              placeId: place.placeId || null
            }));
          } else if (messageText && typeof messageText === 'string') {
            // Ignorar mensajes de estado del backend (no son recomendaciones)
            const isStatusMessage =
              messageText.toLowerCase().includes('already has') ||
              messageText.toLowerCase().includes('maximum') ||
              messageText.toLowerCase().includes('complete');

            if (!isStatusMessage) {
              parsedServiceAreas = messageText
                .split(',')
                .map((s) => s.trim())
                .filter((s) => s.length > 0)
                .map((placeName, index) => ({
                  id: `place_${index}`,
                  placeName,
                  placeId: null
                }));
            }
          }

          // Filtrar las áreas que ya existen en el perfil actual
          // para evitar duplicación visual después del refresh
          const currentServiceAreas =
            googleProfileInfo.data?.serviceArea?.places?.placeInfos || [];

          const existingPlaceNames = new Set(
            currentServiceAreas.map((p) =>
              (p.placeName || '').toLowerCase().trim()
            )
          );

          const filteredServiceAreas = parsedServiceAreas.filter((place) => {
            const normalizedName = (place.placeName || '').toLowerCase().trim();
            return !existingPlaceNames.has(normalizedName);
          });

          console.log(
            'Recommended service areas after filtering:',
            filteredServiceAreas
          );
          setRecommendedServiceAreas(filteredServiceAreas);
        } catch (error) {
          console.error('Error parsing recommended service areas:', error);
        }
      } else {
        // Si no hay recomendaciones del backend, limpiar el estado
        setRecommendedServiceAreas(null);
      }
    }, [qualityAgentData, googleProfileInfo.data]);

    // useEffect vacío removido - causaba error de referencia

    useImperativeHandle(handleShowQualityAgentRef, () => ({
      handleShowQualityAgent,
      cancelEdit: handleCancel
    }));

    // FUNCIONES Y LÓGICA DESPUÉS DE LOS HOOKS

    const [GBPfields] = useState({
      businessName: false,
      businessCategory: false,
      description: false,
      openingDate: true, // Desactivado - no editable
      phoneNumber: false,
      website: false,
      socialProfiles: true,
      shortName: true,
      businessLocation: true, // to review
      serviceArea: false, // ahora editable
      hours: true, // to review
      specialHours: true,
      moreHours: true,
      fromTheBusiness: true,
      crowd: true,
      planning: true,
      serviceOptions: true,
      // NUEVOS CAMPOS
      reviewFrequency: true,
      googleRating: true,
      numberOfQAs: true,
      answeredAllQAs: true,
      servicesList: false,
      reviewReplies: true,
      numberOfImages: true,
      postFrequency: true,
      postKeywordOptimization: false
    });

    // MOCK de sugerencias del agente para los nuevos campos
    const qualityAgentMock = {
      serviceArea: {
        status: 'bad',
        message: 'The service area is too limited.',
        suggestion: 'Add more cities to expand your coverage.'
      },
      reviewFrequency: {
        status: 'good',
        message: 'The review frequency is OK.'
      },
      googleRating: {
        status: 'bad',
        message: 'The Google rating is low.',
        suggestion: 'Ask satisfied clients to leave a positive review.'
      },
      numberOfQAs: {
        status: 'good',
        message: 'The number of Q&As is OK.'
      },
      answeredAllQAs: {
        status: 'bad',
        message: 'Not all questions have been answered.',
        suggestion: 'Answer all questions to build trust.'
      },
      reviewReplies: {
        status: 'good',
        message: 'You reply to reviews OK.'
      },
      numberOfImages: {
        status: 'bad',
        message: 'There are few images on the profile.',
        suggestion: 'Add more high-quality images to attract clients.'
      },
      postFrequency: {
        status: 'good',
        message: 'The posting frequency is OK.'
      },
      postKeywordOptimization: {
        status: 'bad',
        message: 'Posts are not optimized with keywords.',
        suggestion: 'Include relevant keywords in your posts.'
      }
    };

    const handleCancel = useCallback(() => {
      setIsEditing(false);
      setEditedFields({});

      // Recargar los datos originales para descartar cualquier cambio
      loadGoogleProfile();
    }, [loadGoogleProfile]);

    const handleSave = () => {
      // Verificar si hay cambios antes de guardar
      if (Object.keys(editedFields).length === 0) {
        console.log('No hay cambios para guardar');
        return;
      }

      console.log(
        'Guardando cambios para los siguientes campos:',
        editedFields
      );
      console.log('Valores específicos:', {
        businessName: editedFields?.businessName,
        businessCategory: editedFields?.businessCategory,
        description: editedFields?.description,
        website: editedFields?.website,
        openingDate: editedFields?.openingDate
      });

      setIsEditing(false);

      // Construir solo los campos que han sido modificados
      const editedData = {
        clientId: selectedClient?.id
      };

      // Solo incluir campos que realmente han sido modificados
      if (editedFields.businessName !== undefined) {
        editedData.title = editedFields.businessName;
      }

      if (editedFields.businessCategory !== undefined) {
        editedData.categories = editedFields.businessCategory;
      }

      if (editedFields.description !== undefined) {
        editedData.profile = {
          description: editedFields.description
        };
      }

      if (editedFields.website !== undefined) {
        editedData.websiteUri = editedFields.website;
      }

      if (editedFields.openingDate !== undefined) {
        editedData.openInfo = {
          status: 'OPEN',
          canReopen: true,
          openingDate: editedFields.openingDate
        };
      }

      // Manejar campos personalizados que no son parte de la API estándar de Google
      if (editedFields.postKeywordOptimization !== undefined) {
        // Este campo se almacenará en DynamoDB pero no se enviará a Google Business API
        editedData.posts_info = {
          posts_keywords: editedFields.postKeywordOptimization
        };
      }

      // Agregar otros campos personalizados si han sido modificados
      if (editedFields.reviewFrequency !== undefined) {
        editedData.reviews_info = {
          ...editedData.reviews_info,
          reviewFrecuency: editedFields.reviewFrequency
        };
      }

      if (editedFields.googleRating !== undefined) {
        editedData.reviews_info = {
          ...editedData.reviews_info,
          averageRating: editedFields.googleRating
        };
      }

      if (editedFields.reviewReplies !== undefined) {
        editedData.reviews_info = {
          ...editedData.reviews_info,
          reviewsReplied: editedFields.reviewReplies
        };
      }

      if (editedFields.numberOfImages !== undefined) {
        editedData.images_info = {
          ...editedData.images_info,
          totalMediaItemCount: editedFields.numberOfImages
        };
      }

      if (editedFields.postFrequency !== undefined) {
        editedData.posts_info = {
          ...editedData.posts_info,
          posts_frecuency: editedFields.postFrequency
        };
      }

      console.log('Datos a enviar a la lambda:', editedData);

      // Separar campos estándar de Google de campos personalizados
      const googleStandardFields = {};
      const customFields = {};

      // Campos que van a Google Business API
      if (editedData.title) googleStandardFields.title = editedData.title;
      if (editedData.categories)
        googleStandardFields.categories = editedData.categories;
      if (editedData.profile) googleStandardFields.profile = editedData.profile;
      if (editedData.websiteUri)
        googleStandardFields.websiteUri = editedData.websiteUri;
      if (editedData.openInfo)
        googleStandardFields.openInfo = editedData.openInfo;

      // Campos personalizados que solo van a DynamoDB
      if (editedData.posts_info)
        customFields.posts_info = editedData.posts_info;
      if (editedData.reviews_info)
        customFields.reviews_info = editedData.reviews_info;
      if (editedData.images_info)
        customFields.images_info = editedData.images_info;

      // Agregar campos personalizados al objeto principal
      const finalData = {
        ...googleStandardFields,
        ...customFields,
        clientId: editedData.clientId
      };

      console.log('Campos estándar de Google:', googleStandardFields);
      console.log('Campos personalizados:', customFields);
      console.log('Datos finales a enviar:', finalData);

      const headers = {
        'Content-Type': 'application/json'
      };

      axios
        .put(API_UPDATE_GOOGLE_PROFILE_INFO, finalData, { headers })
        .then((response) => {
          console.log('PUT request response:', response.data);

          // Limpiar campos editados después de guardar exitosamente
          setEditedFields({});

          // Recargar los datos del perfil para mostrar los cambios
          loadGoogleProfile();
        })
        .catch((error) => {
          console.error('An error has ocurred saving the data:', error);
          console.error('Error response:', error.response?.data);

          // En caso de error, mantener el modo de edición
          setIsEditing(true);

          // Mostrar mensaje de error
          setSaveMessage({
            show: true,
            type: 'error',
            message: `Error saving changes: ${
              error.response?.data?.message || error.message
            }`
          });

          // Ocultar mensaje después de 5 segundos
          setTimeout(() => {
            setSaveMessage({ show: false, type: '', message: '' });
          }, 5000);
        });
    };

    const handleFieldChange = (fieldName, value) => {
      console.log(`Campo "${fieldName}" cambiado a:`, value);

      setEditedFields((prevEditedFields) => {
        if (prevEditedFields[fieldName] === value) {
          return prevEditedFields;
        }

        const newEditedFields = {
          ...prevEditedFields,
          [fieldName]: value
        };

        console.log('Estado actualizado de editedFields:', newEditedFields);
        return newEditedFields;
      });
    };

    // Handler para el botón Quality Agent: llama al endpoint y muestra el cuadro con la sugerencia real
    const handleShowQualityAgent = useCallback(async () => {
      setLoadingAgent(true);
      setShowQualityAgent(true);
      setQualityAgentData(null);
      setDismissedNotifications({});
      setLoadingFix({});
      setLoadingDismiss({});
      // Elegir sugerencias aleatorias para los campos que NO están en el endpoint
      const randoms = {};
      Object.keys(agentSuggestions).forEach((key) => {
        if (
          ['postKeywordOptimization', 'serviceArea', 'servicesList'].includes(
            key
          )
        ) {
          const arr = agentSuggestions[key];
          randoms[key] = arr[Math.floor(Math.random() * arr.length)];
        }
      });
      setRandomAgentSuggestions(randoms);
      try {
        const { data } = await axios.post(
          API_ENDPOINT_QUALITY_AGENT,
          { clientId: selectedClient?.id },
          { headers: { 'Content-Type': 'application/json' } }
        );
        setQualityAgentData(data);
      } catch (e) {
        setQualityAgentData(null);
      } finally {
        setLoadingAgent(false);
      }
    }, [selectedClient?.id]);

    // Lógica simple de calidad para la descripción
    const description = googleProfileInfo.data?.profile?.description || '';
    const minLength = 30;
    // El valor visual y de análisis debe ser el fixedDescription si existe
    const visualDescription =
      fixedDescription !== null
        ? fixedDescription
        : googleProfileInfo.data?.profile?.description || '';

    const visualPostsKeywords =
      fixedPostsKeywords !== null
        ? fixedPostsKeywords
        : googleProfileInfo.data?.posts_info?.posts_keywords || '';

    // Usar los datos del endpoint para la notificación de descripción y otros campos
    let isDescriptionGood = false;
    let message = '';
    let suggestion = '';
    // Nuevas sugerencias del endpoint
    let postsKeywordsSuggestion = '';
    let postsFrecuencySuggestion = '';
    let reviewsRepliedSuggestion = '';
    let reviewFrecuencySuggestion = '';
    let totalReviewCountSuggestion = '';
    let averageRatingSuggestion = '';
    let totalMediaItemCountSuggestion = '';
    if (qualityAgentData) {
      // Descripción
      isDescriptionGood = qualityAgentData.status === 'good';
      message = qualityAgentData.messages?.[qualityAgentData.status] || '';
      suggestion =
        qualityAgentData?.recomendations?.profile?.description?.message || '';
      // Posts
      postsKeywordsSuggestion =
        qualityAgentData.recomendations?.posts_info?.posts_keywords?.message ||
        '';
      postsFrecuencySuggestion =
        qualityAgentData.recomendations?.posts_info?.posts_frecuency?.message ||
        '';
      // Reviews
      reviewsRepliedSuggestion =
        qualityAgentData.recomendations?.review_info?.reviewsReplied?.message ||
        '';
      reviewFrecuencySuggestion =
        qualityAgentData.recomendations?.review_info?.reviewFrecuency
          ?.message || '';
      totalReviewCountSuggestion =
        qualityAgentData.recomendations?.review_info?.totalReviewCount
          ?.message || '';
      averageRatingSuggestion =
        qualityAgentData.recomendations?.review_info?.averageRating?.message ||
        '';
      // Imágenes
      totalMediaItemCountSuggestion =
        qualityAgentData.recomendations?.images_info?.totalMediaItemCount
          ?.message || '';
    }

    // Función para manejar la aplicación de una categoría individual
    const handleFixIndividualCategory = async (category) => {
      // Verificar si hay alguna operación en progreso
      if (isAnyOperationInProgress()) {
        console.log(
          'Another operation is already in progress, ignoring fix request'
        );
        return;
      }

      const categoryKey = `category_${category.id}`;
      setLoadingFix((prev) => ({ ...prev, [categoryKey]: true }));

      try {
        console.log('Sending individual category:', category);
        console.log('Sending clientId:', selectedClient?.id);

        // Crear un payload con solo la categoría individual
        const payload = {
          clientId: selectedClient?.id,
          fieldPath: 'recomendations.categories.additionalCategories',
          selectedCategories: [category]
        };

        const response = await axios.post(API_ENDPOINT_FIX_FIELD, payload);

        console.log('Individual category fix response:', response.data);

        // Mostrar mensaje de éxito
        setSaveMessage({
          type: 'success',
          text: `Successfully added "${category.display_name}" to your business profile.`
        });

        // Remover la categoría de la lista de recomendadas
        setRecommendedCategories((prev) =>
          prev.filter((recCategory) => recCategory.id !== category.id)
        );
      } catch (error) {
        console.error('Error fixing individual category:', error);
        setSaveMessage({
          type: 'error',
          text: `Failed to add "${category.display_name}". Please try again.`
        });
      } finally {
        setLoadingFix((prev) => ({ ...prev, [categoryKey]: false }));
      }
    };

    // Función para descartar una categoría individual
    const handleDismissIndividualCategory = async (categoryId) => {
      const categoryKey = `category_${categoryId}`;
      setLoadingDismiss((prev) => ({ ...prev, [categoryKey]: true }));

      try {
        // Simular delay para la animación
        await new Promise((resolve) => setTimeout(resolve, 300));

        // Remover la categoría de la lista de recomendadas
        setRecommendedCategories((prev) =>
          prev.filter((category) => category.id !== categoryId)
        );

        console.log(`Dismissed category with ID: ${categoryId}`);
      } catch (error) {
        console.error('Error dismissing individual category:', error);
      } finally {
        setLoadingDismiss((prev) => ({ ...prev, [categoryKey]: false }));
      }
    };

    // Función para manejar el fix de cualquier campo llamando al endpoint
    const handleFixField = async (fieldPath, additionalPayload = {}) => {
      // Verificar si hay alguna operación en progreso
      if (isAnyOperationInProgress()) {
        console.log(
          'Another operation is already in progress, ignoring fix request'
        );
        return;
      }

      // Para el caso especial de profile.description, usar 'description' como clave
      let loadingKey;
      let notificationKey;

      if (fieldPath === 'profile.description') {
        loadingKey = 'description';
        notificationKey = 'description';
      } else if (fieldPath === 'posts_info.posts_keywords') {
        loadingKey = 'postKeywordOptimization';
        notificationKey = 'postKeywordOptimization';
      } else if (fieldPath === 'serviceItems.descriptionKeywordAnalysis') {
        loadingKey = 'services_general';
        notificationKey = 'services_general';
      } else if (fieldPath === 'serviceItems.recommendedServices') {
        loadingKey = 'recommended_services_general';
        notificationKey = 'recommended_services_general';
      } else if (fieldPath === 'categories.additionalCategories') {
        loadingKey = 'recommended_categories';
        notificationKey = 'recommended_categories';
      } else if (fieldPath === 'serviceArea.places.placeInfos') {
        loadingKey = 'serviceArea';
        notificationKey = 'serviceArea';
      } else if (fieldPath.startsWith('serviceArea.places.placeInfos.')) {
        // Para service areas individuales: serviceArea.places.placeInfos.{placeId}
        const placeId = additionalPayload?.placeId || 'place';
        loadingKey = `recommended_service_area_${placeId}`;
        notificationKey = `recommended_service_area_${placeId}`;
      } else if (
        fieldPath.startsWith('serviceItems.') &&
        fieldPath.endsWith('.description')
      ) {
        // Para servicios individuales: serviceItems.{index}.description
        const serviceName = additionalPayload?.serviceName || 'service';
        loadingKey = `service_${serviceName}_individual`;
        notificationKey = `service_${serviceName}_individual`;
      } else {
        // Extraer la clave base del fieldPath (antes del primer punto)
        loadingKey = fieldPath.split('.')[0];
        notificationKey = fieldPath.split('.')[0];
      }

      setLoadingFix((prev) => ({ ...prev, [loadingKey]: true }));

      try {
        console.log('Sending clientId:', selectedClient?.id);
        console.log('Sending fieldPath:', `recomendations.${fieldPath}`);

        const response = await axios.post(API_ENDPOINT_FIX_FIELD, {
          clientId: selectedClient?.id,
          fieldPath: `recomendations.${fieldPath}`,
          ...additionalPayload
        });

        // mock response with delay
        // const response = await new Promise((resolve) => {
        //   setTimeout(() => {
        //     // Mock response específico para cada tipo de campo
        //     let mockData;
        //     if (fieldPath === 'serviceArea.places.placeInfos') {
        //       mockData = {
        //         message: 'Service area fixed successfully',
        //         serviceAreaRecommendations:
        //           qualityAgentData?.recomendations?.serviceArea?.places
        //             ?.placeInfos || []
        //       };
        //     } else if (fieldPath === 'profile.description') {
        //       mockData = {
        //         message: 'Description fixed successfully',
        //         description:
        //           qualityAgentData?.recomendations?.profile?.description
        //             ?.message || ''
        //       };
        //     } else if (fieldPath === 'posts_info.posts_keywords') {
        //       mockData = {
        //         message: 'Posts keywords fixed successfully',
        //         postsKeywords:
        //           qualityAgentData?.recomendations?.posts_info?.posts_keywords
        //             ?.message || ''
        //       };
        //     } else {
        //       mockData = {
        //         message: 'Field fixed successfully'
        //       };
        //     }

        //     resolve({
        //       status: 200,
        //       data: mockData
        //     });
        //   }, 5000); // Delay de 5 segundos
        // });

        console.log('Fix field response:', response.data);
        console.log('Field path:', fieldPath);
        console.log(
          'Response data for service area:',
          response.data.serviceAreaRecommendations
        );

        // Solo cerrar la notificación si el status es 200
        if (response.status === 200) {
          if (fieldPath === 'serviceItems.descriptionKeywordAnalysis') {
            // Para el fix general de servicios, cerrar también todas las notificaciones individuales
            const servicesData = findServicesField(googleProfileInfo.data);
            const currentServices = processServicesList(servicesData);

            if (currentServices) {
              const dismissUpdates = {};

              // Cerrar la notificación general
              dismissUpdates[notificationKey] = true;

              // Cerrar todas las notificaciones individuales de servicios
              currentServices.forEach((service) => {
                dismissUpdates[`service_${service.name}`] = true;
              });

              setDismissedNotifications((prev) => ({
                ...prev,
                ...dismissUpdates
              }));
            } else {
              // Fallback: solo cerrar la notificación general
              setDismissedNotifications((prev) => ({
                ...prev,
                [notificationKey]: true
              }));
            }
          } else {
            // Para otros campos, comportamiento normal
            setDismissedNotifications((prev) => ({
              ...prev,
              [notificationKey]: true
            }));
          }

          // Actualizar los valores de los campos con las recomendaciones del quality agent
          if (fieldPath === 'profile.description') {
            setFixedDescription(
              response.data.description ||
                qualityAgentData?.recomendations?.profile?.description
                  ?.message ||
                ''
            );
          } else if (fieldPath === 'posts_info.posts_keywords') {
            setFixedPostsKeywords(
              response.data.postsKeywords ||
                qualityAgentData?.recomendations?.posts_info?.posts_keywords
                  ?.message ||
                ''
            );
          } else if (fieldPath === 'serviceItems.descriptionKeywordAnalysis') {
            // Obtener los servicios actuales
            const servicesData = findServicesField(googleProfileInfo.data);
            const currentServices = processServicesList(servicesData);

            console.log('Current services before fix:', currentServices);
            console.log('Quality agent data:', qualityAgentData);
            console.log(
              'Service recommendations available:',
              qualityAgentData?.recomendations?.serviceItems
                ?.descriptionKeywordAnalysis?.message
            );

            if (
              currentServices &&
              qualityAgentData?.recomendations?.serviceItems
                ?.descriptionKeywordAnalysis?.message
            ) {
              try {
                const rawMessage =
                  qualityAgentData.recomendations.serviceItems
                    .descriptionKeywordAnalysis.message;
                const parsedMessage = parseServiceRecommendations(rawMessage);

                console.log('Parsed recommendations:', parsedMessage);

                // Crear una nueva lista de servicios con las descripciones actualizadas
                const updatedServices = currentServices.map((service) => {
                  // Buscar la recomendación para este servicio
                  const serviceRecommendation = parsedMessage.find(
                    (item) =>
                      item.freeFormServiceItem?.label?.displayName ===
                        service.name ||
                      item.structuredServiceItem?.label?.displayName ===
                        service.name
                  );

                  console.log(`Service: ${service.name}`, {
                    hasDescription: !!service.description,
                    hasRecommendation: !!serviceRecommendation,
                    freeFormRecommendation:
                      serviceRecommendation?.freeFormServiceItem?.label
                        ?.description,
                    structuredRecommendation:
                      serviceRecommendation?.structuredServiceItem?.label
                        ?.description
                  });

                  // Si el servicio no tiene descripción y hay una recomendación, usar la recomendación
                  const freeFormDescription =
                    serviceRecommendation?.freeFormServiceItem?.label
                      ?.description;
                  const structuredDescription =
                    serviceRecommendation?.structuredServiceItem?.label
                      ?.description;

                  if (
                    (!service.description ||
                      service.description.trim() === '') &&
                    (freeFormDescription || structuredDescription)
                  ) {
                    const descriptionToUse =
                      freeFormDescription || structuredDescription;
                    const recommendationType = freeFormDescription
                      ? 'freeForm'
                      : 'structured';

                    console.log(
                      `Applying ${recommendationType} description to ${service.name}:`,
                      descriptionToUse
                    );
                    return {
                      ...service,
                      description: descriptionToUse
                    };
                  }

                  return service;
                });

                console.log('Updated services after fix:', updatedServices);

                // Actualizar el estado con los servicios que tienen descripciones
                setFixedServicesList(updatedServices);
              } catch (error) {
                console.error('Error parsing service recommendations:', error);
              }
            } else {
              console.log('No current services or recommendations available');
            }
          } else if (fieldPath === 'serviceArea.places.placeInfos') {
            // Obtener las recomendaciones de service area del response o del qualityAgentData
            const serviceAreaRecommendations =
              response.data.serviceAreaRecommendations ||
              qualityAgentData?.recomendations?.serviceArea?.places?.placeInfos;

            console.log(
              'Service area fix response:',
              response.data,
              'Recommendations:',
              serviceAreaRecommendations
            );

            if (
              serviceAreaRecommendations &&
              serviceAreaRecommendations.length > 0
            ) {
              console.log(
                'Service area recommendations:',
                serviceAreaRecommendations
              );

              // Actualizar el estado con las áreas de servicio recomendadas
              console.log(
                'Setting fixedServiceArea with:',
                serviceAreaRecommendations
              );
              // Crear una nueva referencia para forzar el re-renderizado
              setFixedServiceArea([...serviceAreaRecommendations]);

              // Cerrar la notificación
              setDismissedNotifications((prev) => ({
                ...prev,
                serviceArea: true
              }));

              // Recargar los datos del perfil para reflejar los cambios
              loadGoogleProfile();
            } else {
              console.warn('No service area recommendations found in response');
            }
          } else if (fieldPath.startsWith('serviceArea.places.placeInfos.')) {
            // Manejar el fix de una service area individual
            const placeId = additionalPayload?.placeId;
            const placeName = additionalPayload?.placeName;

            console.log(
              `Fixing individual service area: ${placeName} with ID ${placeId}`
            );

            // Obtener las áreas de servicio actuales
            const currentServiceAreas =
              googleProfileInfo.data?.serviceArea?.places?.placeInfos || [];

            // Agregar la nueva área de servicio
            const newServiceArea = {
              placeName: placeName,
              placeId: placeId
              // Puedes agregar más campos si los necesitas
            };

            // Verificar si ya existe para evitar duplicados
            const alreadyExists = currentServiceAreas.some(
              (area) => area.placeName === placeName
            );

            if (!alreadyExists) {
              const updatedServiceAreas = [
                ...currentServiceAreas,
                newServiceArea
              ];

              console.log('Updated service areas:', updatedServiceAreas);

              // Actualizar el estado con las áreas de servicio actualizadas
              setFixedServiceArea(updatedServiceAreas);

              // Cerrar la notificación de esta service area específica
              setDismissedNotifications((prev) => ({
                ...prev,
                [`recommended_service_area_${placeId}`]: true
              }));

              // Recargar los datos del perfil para reflejar los cambios
              loadGoogleProfile();
            } else {
              console.log(`Service area ${placeName} already exists`);
            }
          } else if (fieldPath === 'serviceItems.recommendedServices') {
            // Manejar el fix de servicios recomendados
            console.log('Fixing recommended services');

            // Cerrar la notificación general de servicios recomendados
            setDismissedNotifications((prev) => ({
              ...prev,
              recommended_services_general: true
            }));

            // Cerrar todas las notificaciones individuales de servicios recomendados
            if (recommendedServices && recommendedServices.length > 0) {
              const dismissUpdates = {};
              recommendedServices.forEach((service) => {
                dismissUpdates[`recommended_service_${service.id}`] = true;
              });
              setDismissedNotifications((prev) => ({
                ...prev,
                ...dismissUpdates
              }));
            }

            // Aquí podrías agregar lógica para agregar los servicios recomendados
            // a la lista de servicios existentes del perfil
            console.log(
              'All recommended services have been added to the profile'
            );
          } else if (
            fieldPath.startsWith('serviceItems.') &&
            fieldPath.endsWith('.description')
          ) {
            // Manejar el fix de un servicio individual
            const serviceName = additionalPayload?.serviceName;
            const serviceIndex = additionalPayload?.serviceIndex;

            console.log(
              `Fixing individual service: ${serviceName} at index ${serviceIndex}`
            );

            // Obtener la descripción actualizada del response
            let updatedDescription = response.data.description;

            // Si la lambda devuelve la descripción directamente, usarla
            if (!updatedDescription && response.data.updatedService) {
              if (
                response.data.updatedService.structuredServiceItem?.description
              ) {
                updatedDescription =
                  response.data.updatedService.structuredServiceItem
                    .description;
              } else if (
                response.data.updatedService.freeFormServiceItem?.label
                  ?.description
              ) {
                updatedDescription =
                  response.data.updatedService.freeFormServiceItem.label
                    .description;
              }
            }

            // Fallback: buscar en las recomendaciones si no viene en el response
            if (
              !updatedDescription &&
              qualityAgentData?.recomendations?.serviceItems
                ?.descriptionKeywordAnalysis?.message
            ) {
              try {
                const rawMessage =
                  qualityAgentData.recomendations.serviceItems
                    .descriptionKeywordAnalysis.message;
                const parsedMessage = parseServiceRecommendations(rawMessage);

                // Buscar la recomendación específica para este servicio
                const serviceRecommendation = parsedMessage.find(
                  (item) =>
                    item.freeFormServiceItem?.label?.displayName ===
                      serviceName ||
                    item.structuredServiceItem?.label?.displayName ===
                      serviceName
                );

                if (serviceRecommendation) {
                  updatedDescription =
                    serviceRecommendation.freeFormServiceItem?.label
                      ?.description ||
                    serviceRecommendation.structuredServiceItem?.description ||
                    serviceRecommendation.structuredServiceItem?.label
                      ?.description;
                }
              } catch (error) {
                console.error(
                  'Error parsing individual service recommendation:',
                  error
                );
              }
            }

            if (updatedDescription) {
              // Actualizar la lista de servicios con la nueva descripción
              setFixedServicesList((prevServices) => {
                // Si no hay servicios fijos previos, inicializar con los servicios actuales
                let currentServicesList = prevServices;
                if (!currentServicesList) {
                  const servicesData = findServicesField(
                    googleProfileInfo.data
                  );
                  currentServicesList = processServicesList(servicesData) || [];
                }

                return currentServicesList.map((service, index) => {
                  if (index === serviceIndex && service.name === serviceName) {
                    return {
                      ...service,
                      description: updatedDescription
                    };
                  }
                  return service;
                });
              });

              // Cerrar tanto la notificación individual como la general
              // La notificación general se cierra porque el usuario ya empezó a hacer fixes individuales
              setDismissedNotifications((prev) => ({
                ...prev,
                [`service_${serviceName}_bad`]: true,
                services_general: true
              }));

              console.log(
                `Successfully updated description for service: ${serviceName}`
              );
              console.log('Updated description:', updatedDescription);
              console.log(
                'General services notification dismissed due to individual fix'
              );
            }
          }
        }
      } catch (error) {
        console.error('Error calling fix field endpoint:', error);
        console.error('Error response:', error.response?.data);
        // Aquí podrías mostrar un mensaje de error al usuario
      } finally {
        setLoadingFix((prev) => ({ ...prev, [loadingKey]: false }));
      }
    };

    // Mostrar notificaciones del agente solo cuando showQualityAgent es true Y loadingAgent es false
    const showAgentNotifications = showQualityAgent && !loadingAgent;

    const handleDismissNotification = async (field) => {
      console.log('Dismissing notification for field:', field);
      const loadingKey = field;

      if (
        field === 'numberOfImages' ||
        field === 'postFrequency' ||
        field === 'googleRating' ||
        field === 'reviewReplies' ||
        field === 'reviewFrequency' ||
        field === 'postFrequency' ||
        field.startsWith('service_') ||
        field.startsWith('recommended_service_') ||
        field.startsWith('recommended_service_area_')
      ) {
        setDismissedNotifications((prev) => ({ ...prev, [field]: true }));
        return;
      }
      setLoadingDismiss((prev) => ({ ...prev, [loadingKey]: true }));
      try {
        console.log('Sending dismiss for field:', field);
        console.log('Sending clientId:', selectedClient?.id);

        // Mapear el nombre del campo al fieldPath correcto
        let fieldPath;
        if (field === 'description') {
          fieldPath = 'profile.description';
        } else if (field.startsWith('service_')) {
          const serviceIndex = field.split('_')[1];
          fieldPath = `serviceItems.${serviceIndex}.description`;
        } else if (field.startsWith('recommended_service_')) {
          const serviceId = field.replace('recommended_service_', '');
          fieldPath = `serviceItems.recommendedServices.${serviceId}`;
        } else if (field === 'recommended_services_general') {
          fieldPath = 'serviceItems.recommendedServices';
        } else {
          // Para otros campos, usar el nombre del campo directamente
          fieldPath = field;
        }

        const response = await axios.post(API_ENDPOINT_DISMISS_FIELD, {
          clientId: selectedClient?.id,
          fieldPath: `recomendations.${fieldPath}`
        });

        console.log('Dismiss field response:', response);

        console.log('Dismiss field response:', response.data);

        // Solo cerrar la notificación si el status es 200
        if (response.status === 200) {
          if (field === 'services_general') {
            // Si es el dismiss general de servicios, cerrar también todas las notificaciones individuales
            const servicesData = findServicesField(googleProfileInfo.data);
            const currentServices = processServicesList(servicesData);

            if (currentServices) {
              const dismissUpdates = {};

              // Cerrar la notificación general
              dismissUpdates[field] = true;

              // Cerrar todas las notificaciones individuales de servicios
              currentServices.forEach((_, index) => {
                dismissUpdates[`service_${index}`] = true;
              });

              setDismissedNotifications((prev) => ({
                ...prev,
                ...dismissUpdates
              }));
            } else {
              // Fallback: solo cerrar la notificación general
              setDismissedNotifications((prev) => ({ ...prev, [field]: true }));
            }
          } else if (field === 'recommended_services_general') {
            // Si es el dismiss general de servicios recomendados, cerrar también todas las notificaciones individuales
            if (recommendedServices && recommendedServices.length > 0) {
              const dismissUpdates = {};

              // Cerrar la notificación general
              dismissUpdates[field] = true;

              // Cerrar todas las notificaciones individuales de servicios recomendados
              recommendedServices.forEach((service) => {
                dismissUpdates[`recommended_service_${service.id}`] = true;
              });

              setDismissedNotifications((prev) => ({
                ...prev,
                ...dismissUpdates
              }));
            } else {
              // Fallback: solo cerrar la notificación general
              setDismissedNotifications((prev) => ({ ...prev, [field]: true }));
            }
          } else {
            // Para otros campos, comportamiento normal
            setDismissedNotifications((prev) => ({ ...prev, [field]: true }));
          }
        }
      } catch (error) {
        console.error('Error calling dismiss field endpoint:', error);
        console.error('Error response:', error.response?.data);
        // Aún cerrar la notificación localmente en caso de error
        if (field === 'services_general') {
          // Si es el dismiss general de servicios, cerrar también todas las notificaciones individuales
          const servicesData = findServicesField(googleProfileInfo.data);
          const currentServices = processServicesList(servicesData);

          if (currentServices) {
            const dismissUpdates = {};

            // Cerrar la notificación general
            dismissUpdates[field] = true;

            // Cerrar todas las notificaciones individuales de servicios
            currentServices.forEach((_, index) => {
              dismissUpdates[`service_${index}`] = true;
            });

            setDismissedNotifications((prev) => ({
              ...prev,
              ...dismissUpdates
            }));
          } else {
            // Fallback: solo cerrar la notificación general
            setDismissedNotifications((prev) => ({ ...prev, [field]: true }));
          }
        } else if (field === 'recommended_services_general') {
          // Si es el dismiss general de servicios recomendados, cerrar también todas las notificaciones individuales
          if (recommendedServices && recommendedServices.length > 0) {
            const dismissUpdates = {};

            // Cerrar la notificación general
            dismissUpdates[field] = true;

            // Cerrar todas las notificaciones individuales de servicios recomendados
            recommendedServices.forEach((service) => {
              dismissUpdates[`recommended_service_${service.id}`] = true;
            });

            setDismissedNotifications((prev) => ({
              ...prev,
              ...dismissUpdates
            }));
          } else {
            // Fallback: solo cerrar la notificación general
            setDismissedNotifications((prev) => ({ ...prev, [field]: true }));
          }
        } else {
          // Para otros campos, comportamiento normal
          setDismissedNotifications((prev) => ({ ...prev, [field]: true }));
        }
      } finally {
        setLoadingDismiss((prev) => ({ ...prev, [loadingKey]: false }));
      }
    };

    // Función para manejar el fix de descripción de servicios individuales
    const handleFixServiceDescription = async (serviceIndex, serviceName) => {
      const loadingKey = `service_${serviceIndex}`;

      setLoadingFix((prev) => ({ ...prev, [loadingKey]: true }));

      try {
        console.log('Sending clientId:', selectedClient?.id);
        console.log('Sending serviceIndex:', serviceIndex);
        console.log('Sending serviceName:', serviceName);

        const response = await axios.post(API_ENDPOINT_FIX_FIELD, {
          clientId: selectedClient?.id,
          fieldPath: `recomendations.serviceItems.${serviceIndex}.description`,
          serviceName: serviceName
        });

        console.log('Fix service description response:', response.data);

        // Solo cerrar la notificación si el status es 200
        if (response.status === 200) {
          setDismissedNotifications((prev) => ({
            ...prev,
            [loadingKey]: true
          }));
        }
      } catch (error) {
        console.error('Error calling fix service description endpoint:', error);
        console.error('Error response:', error.response?.data);
      } finally {
        setLoadingFix((prev) => ({ ...prev, [loadingKey]: false }));
      }
    };

    // Función para manejar el fix de service area individual
    const handleFixRecommendedServiceArea = async (placeId, placeName) => {
      // Verificar si hay alguna operación en progreso
      if (isAnyOperationInProgress()) {
        console.log(
          'Another operation is in progress. Please wait before adding a service area.'
        );
        return;
      }

      const loadingKey = `recommended_service_area_${placeId}`;

      setLoadingFix((prev) => ({ ...prev, [loadingKey]: true }));

      try {
        const fieldPath = `serviceArea.places.placeInfos.${placeId}`;
        await handleFixField(fieldPath, { placeId, placeName });

        // Remover inmediatamente la recomendación de la lista para evitar duplicación visual
        setRecommendedServiceAreas((prev) =>
          prev ? prev.filter((place) => place.id !== placeId) : null
        );

        // Limpiar fixedServiceArea para evitar duplicación con áreas del perfil
        setFixedServiceArea(null);

        // Recargar el perfil para obtener la data actualizada de Google
        await loadGoogleProfile();

        // Limpiar el estado de áreas añadidas
        setAddedRecommendedServiceAreas(new Set());
      } catch (error) {
        console.error('Error adding service area:', error);
      } finally {
        setLoadingFix((prev) => ({ ...prev, [loadingKey]: false }));
      }
    };

    // Función para manejar el fix de servicios recomendados
    const handleFixRecommendedService = async (serviceId) => {
      // Verificar si hay alguna operación en progreso
      if (isAnyOperationInProgress()) {
        console.log(
          'Another operation is already in progress, ignoring fix recommended service request'
        );
        return;
      }

      const loadingKey = `recommended_service_${serviceId}`;

      setLoadingFix((prev) => ({ ...prev, [loadingKey]: true }));

      try {
        console.log('Sending clientId:', selectedClient?.id);
        console.log('Sending serviceId:', serviceId);

        const response = await axios.post(API_ENDPOINT_FIX_FIELD, {
          clientId: selectedClient?.id,
          fieldPath: `recomendations.serviceItems.recommendedServices.${serviceId}`,
          serviceId: serviceId
        });

        console.log('Fix recommended service response:', response.data);

        // Solo cerrar la notificación si el status es 200
        if (response.status === 200) {
          setDismissedNotifications((prev) => ({
            ...prev,
            [loadingKey]: true,
            // También cerrar la notificación general de servicios recomendados
            recommended_services_general: true
          }));

          // Agregar el servicio recomendado a la lista de servicios existentes
          if (recommendedServices) {
            const serviceToAdd = recommendedServices.find(
              (s) => s.id === serviceId
            );
            if (serviceToAdd) {
              // Aquí podrías agregar el servicio a la lista existente
              // Por ahora solo cerramos la notificación
              console.log('Service added:', serviceToAdd);
            }
          }
        }
      } catch (error) {
        console.error('Error calling fix recommended service endpoint:', error);
        console.error('Error response:', error.response?.data);
      } finally {
        setLoadingFix((prev) => ({ ...prev, [loadingKey]: false }));
      }
    };

    // FUNCIÓN HELPER PARA PARSEAR RECOMENDACIONES DE SERVICIOS
    const parseServiceRecommendations = (rawMessage) => {
      try {
        return JSON.parse(rawMessage);
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
        console.log(
          'Raw message (first 200 chars):',
          rawMessage.substring(0, 200)
        );
        console.log('Raw message length:', rawMessage.length);
        return [];
      }
    };

    // FUNCIÓN HELPER PARA PARSEAR SERVICIOS RECOMENDADOS
    const parseRecommendedServices = (rawMessage) => {
      try {
        const parsed = JSON.parse(rawMessage);
        if (Array.isArray(parsed)) {
          return parsed
            .map((item, index) => {
              if (item.freeFormServiceItem && item.freeFormServiceItem.label) {
                return {
                  id: `recommended_${index}`,
                  name: item.freeFormServiceItem.label.displayName,
                  description: item.freeFormServiceItem.label.description,
                  category: item.freeFormServiceItem.category,
                  isRecommended: true
                };
              }
              return null;
            })
            .filter(Boolean);
        }
        return [];
      } catch (parseError) {
        console.error('JSON parse error for recommended services:', parseError);
        console.log(
          'Raw message (first 200 chars):',
          rawMessage.substring(0, 200)
        );
        return [];
      }
    };

    // FUNCIÓN PARA BUSCAR EL CAMPO DE SERVICIOS
    const findServicesField = (data) => {
      if (!data) return null;

      // Primero, verificar el campo estándar de Google Business Profile: serviceItems
      if (data.serviceItems && Array.isArray(data.serviceItems)) {
        // console.log('Found serviceItems:', data.serviceItems);
        return data.serviceItems;
      }

      // Buscar campos que puedan contener servicios
      const possibleFields = Object.keys(data).filter(
        (key) =>
          key.toLowerCase().includes('service') ||
          key.toLowerCase().includes('servicio') ||
          key.toLowerCase().includes('services')
      );

      console.log('Possible service fields:', possibleFields);

      // Verificar cada campo para ver si contiene datos de servicios
      for (const field of possibleFields) {
        const fieldData = data[field];

        if (Array.isArray(fieldData) && fieldData.length > 0) {
          const firstItem = fieldData[0];
          if (
            firstItem.structuredServiceItem ||
            firstItem.freeFormServiceItem
          ) {
            // console.log(`Found services in field: ${field}`, fieldData);
            return fieldData;
          }
        }
      }

      // Si no encontramos nada, buscar en el campo servicesList directamente
      if (data.servicesList && Array.isArray(data.servicesList)) {
        // console.log('Found servicesList:', data.servicesList);
        return data.servicesList;
      }

      console.log('No services found in data');
      return null;
    };

    // FUNCIÓN PARA PROCESAR LA LISTA DE SERVICIOS
    const processServicesList = (servicesData) => {
      if (!servicesData || !Array.isArray(servicesData)) {
        return null;
      }

      const processedServices = [];

      servicesData.forEach((service) => {
        if (service.structuredServiceItem) {
          const { label, serviceTypeId, description } =
            service.structuredServiceItem;

          // Caso 1: structuredServiceItem con label (estructura de recomendaciones)
          if (label && label.displayName) {
            processedServices.push({
              name: label.displayName,
              description: label.description || ''
            });
          }
          // Caso 2: structuredServiceItem con serviceTypeId (estructura real de Google Business Profile)
          else if (serviceTypeId) {
            // Extraer el nombre del servicio del serviceTypeId y hacerlo más legible
            const serviceName = serviceTypeId
              .replace('job_type_id:', '')
              .replace(/_/g, ' ')
              .split(' ')
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');

            processedServices.push({
              name: serviceName,
              description: description || '' // Usar la descripción directa del structuredServiceItem si existe
            });
          }
        } else if (service.freeFormServiceItem) {
          const { label } = service.freeFormServiceItem;
          if (label && label.displayName) {
            processedServices.push({
              name: label.displayName,
              description: label.description || ''
            });
          }
        }
      });

      return processedServices;
    };

    // FUNCIÓN PARA CLASIFICAR SERVICIOS POR CATEGORÍAS
    const classifyServicesByCategory = (
      servicesData,
      recommendedServices = []
    ) => {
      const servicesByCategory = {};

      // Procesar servicios existentes solo si existen
      if (servicesData && Array.isArray(servicesData)) {
        servicesData.forEach((service) => {
          let categoryName = 'Other Services';
          let serviceInfo = null;

          if (service.freeFormServiceItem) {
            const { category, label } = service.freeFormServiceItem;
            if (category) {
              // Buscar la categoría existente que coincida con este ID
              let foundCategory = null;

              // Verificar categoría principal
              if (
                googleProfileInfo.data?.categories?.primaryCategory?.name ===
                category
              ) {
                foundCategory =
                  googleProfileInfo.data.categories.primaryCategory.displayName;
              }

              // Verificar categorías adicionales
              if (
                !foundCategory &&
                googleProfileInfo.data?.categories?.additionalCategories
              ) {
                const additionalCategory =
                  googleProfileInfo.data.categories.additionalCategories.find(
                    (cat) => cat.name === category
                  );
                if (additionalCategory) {
                  foundCategory = additionalCategory.displayName;
                }
              }

              // Si se encuentra una categoría existente, usar su displayName
              if (foundCategory) {
                categoryName = foundCategory;
              } else {
                // Si no se encuentra, generar nombre desde el ID como fallback
                categoryName = category
                  .replace('categories/gcid:', '')
                  .replace(/_/g, ' ')
                  .split(' ')
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(' ');
              }
            } else {
              // Si no tiene categoría específica, usar la categoría principal del negocio
              categoryName =
                googleProfileInfo.data?.categories?.primaryCategory
                  ?.displayName || 'Other Services';
            }

            if (label && label.displayName) {
              serviceInfo = {
                name: label.displayName,
                description: label.description || '',
                type: 'freeForm'
              };
            }
          } else if (service.structuredServiceItem) {
            const { serviceTypeId, description, category } =
              service.structuredServiceItem;

            // Verificar si el servicio estructurado tiene categoría específica
            if (category) {
              // Extraer el nombre de la categoría del ID
              categoryName = category
                .replace('categories/gcid:', '')
                .replace(/_/g, ' ')
                .split(' ')
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
            } else {
              // Buscar en additionalCategories para hacer match con serviceTypeId
              const additionalCategories =
                googleProfileInfo.data?.categories?.additionalCategories || [];
              let foundCategory = null;

              // Buscar en cada categoría adicional
              for (const additionalCategory of additionalCategories) {
                if (additionalCategory.serviceTypes) {
                  const matchingServiceType =
                    additionalCategory.serviceTypes.find(
                      (serviceType) =>
                        serviceType.serviceTypeId === serviceTypeId
                    );
                  if (matchingServiceType) {
                    foundCategory = additionalCategory.displayName;
                    break;
                  }
                }
              }

              // Usar la categoría encontrada o la categoría principal del negocio
              categoryName =
                foundCategory ||
                googleProfileInfo.data?.categories?.primaryCategory
                  ?.displayName ||
                'Other Services';
            }

            const serviceName = serviceTypeId
              .replace('job_type_id:', '')
              .replace(/_/g, ' ')
              .split(' ')
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');

            serviceInfo = {
              name: serviceName,
              description: description || '',
              type: 'structured'
            };
          }

          if (serviceInfo) {
            if (!servicesByCategory[categoryName]) {
              servicesByCategory[categoryName] = [];
            }
            servicesByCategory[categoryName].push(serviceInfo);
          }
        });
      }

      // Procesar servicios recomendados (SIEMPRE, incluso si no hay servicios existentes)
      if (recommendedServices && recommendedServices.length > 0) {
        console.log('Processing recommended services:', recommendedServices);
        recommendedServices.forEach((recommendedService) => {
          let categoryName = 'Other Services';

          console.log('Processing recommended service:', recommendedService);

          if (recommendedService.category) {
            // Normalizar el ID de categoría para hacer match con las categorías existentes
            const normalizedCategoryId = recommendedService.category.startsWith(
              'gcid:'
            )
              ? `categories/${recommendedService.category}`
              : recommendedService.category;

            console.log('Normalized category ID:', normalizedCategoryId);

            // Buscar la categoría existente que coincida
            let foundCategory = null;

            // Verificar categoría principal
            if (
              googleProfileInfo.data?.categories?.primaryCategory?.name ===
              normalizedCategoryId
            ) {
              foundCategory =
                googleProfileInfo.data.categories.primaryCategory.displayName;
            }

            // Verificar categorías adicionales
            if (
              !foundCategory &&
              googleProfileInfo.data?.categories?.additionalCategories
            ) {
              const additionalCategory =
                googleProfileInfo.data.categories.additionalCategories.find(
                  (cat) => cat.name === normalizedCategoryId
                );
              if (additionalCategory) {
                foundCategory = additionalCategory.displayName;
              }
            }

            // Si se encuentra una categoría existente, usar su displayName
            if (foundCategory) {
              categoryName = foundCategory;
              console.log('Using existing category:', categoryName);
            } else {
              // Si no se encuentra, usar la categoría principal como fallback
              categoryName =
                googleProfileInfo.data?.categories?.primaryCategory
                  ?.displayName || 'Other Services';
              console.log(
                'Category not found, using primary category:',
                categoryName
              );
            }
          } else {
            // Si no tiene categoría específica, usar la categoría principal del negocio
            categoryName =
              googleProfileInfo.data?.categories?.primaryCategory
                ?.displayName || 'Other Services';
            console.log('Using primary category:', categoryName);
          }

          const serviceInfo = {
            id: recommendedService.id, // Incluir el ID del servicio recomendado
            name: recommendedService.name,
            description: recommendedService.description || '',
            type: 'recommended',
            isRecommended: true
          };

          // console.log('Adding service to category:', categoryName, serviceInfo);

          if (!servicesByCategory[categoryName]) {
            servicesByCategory[categoryName] = [];
          }
          servicesByCategory[categoryName].push(serviceInfo);
        });
      } else {
        // console.log('No recommended services to process');
      }

      // console.log('Final servicesByCategory:', servicesByCategory);
      return servicesByCategory;
    };

    // FUNCIÓN PARA RENDERIZAR LA LISTA DE SERVICIOS AGRUPADOS POR CATEGORÍA
    const renderServicesList = (originalServicesData, allServices) => {
      if (!allServices || allServices.length === 0) {
        return <i className="fas fa-minus"></i>;
      }

      // Usar directamente los allServices que se pasan como parámetro
      // Estos ya incluyen servicios recomendados y descripciones actualizadas

      // Obtener servicios recomendados de allServices
      const recommendedServicesList = allServices.filter(
        (service) => service.isRecommended
      );

      // Clasificar servicios por categoría usando los datos originales y servicios recomendados
      // console.log('Original services data:', originalServicesData);
      // console.log(
      //   'Recommended services for classification:',
      //   recommendedServicesList
      // );

      const servicesByCategory = classifyServicesByCategory(
        originalServicesData,
        recommendedServicesList
      );
      // console.log('Services by category:', servicesByCategory);

      const categoryNames = Object.keys(servicesByCategory);

      if (categoryNames.length === 0) {
        return <i className="fas fa-minus"></i>;
      }

      return (
        <div>
          {categoryNames.map((categoryName) => {
            const categoryServices = servicesByCategory[categoryName];

            // Usar directamente los servicios de la categoría (ya incluyen servicios recomendados)
            const processedCategoryServices = categoryServices;

            // Filtrar servicios visibles para el contador
            const visibleServices = processedCategoryServices.filter(
              (service) => {
                // Si es un servicio recomendado y ha sido dismissado, no mostrarlo
                if (service.isRecommended) {
                  const isDismissed =
                    dismissedNotifications[
                      `service_${service.name}_recommended`
                    ];
                  return !isDismissed;
                }
                // Mostrar todos los servicios existentes
                return true;
              }
            );

            const serviceCount = visibleServices.length;

            return (
              <div key={categoryName} className="services-category-section">
                <div className="category-header">
                  <span
                    style={{
                      backgroundColor: '#d1ecf1',
                      color: '#0c5460',
                      fontSize: '0.75rem',
                      fontWeight: '500',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      marginBottom: '12px',
                      display: 'inline-block',
                      border: '1px solid #bee5eb'
                    }}>
                    {categoryName} ({serviceCount})
                  </span>
                </div>
                <div className="services-list">
                  {visibleServices
                    .map((service, index) => {
                      // Aplicar descripciones actualizadas si están disponibles
                      let serviceToRender = service;
                      if (allServices && !service.isRecommended) {
                        const updatedService = allServices.find(
                          (updated) => updated.name === service.name
                        );
                        if (updatedService) {
                          serviceToRender = {
                            ...service,
                            description: updatedService.description
                          };
                        }
                      }
                      return serviceToRender;
                    })
                    .map((service, index) => (
                      <div
                        key={`${service.name}-${index}`}
                        className={`service-item ${
                          service.isRecommended &&
                          !addedRecommendedServices.has(service.name)
                            ? 'recommended-service'
                            : ''
                        }`}
                        style={
                          service.isRecommended &&
                          !addedRecommendedServices.has(service.name)
                            ? {
                                border: '2px solid #007bff',
                                borderRadius: '8px',
                                padding: '12px',
                                marginBottom: '12px',
                                backgroundColor: '#f8f9ff'
                              }
                            : {
                                border: '1px solid #e9ecef',
                                borderRadius: '8px',
                                padding: '12px',
                                marginBottom: '12px',
                                backgroundColor: '#fff'
                              }
                        }>
                        <div className="d-flex align-items-center justify-content-between">
                          <strong>{service.name}</strong>
                          {service.isRecommended &&
                            !addedRecommendedServices.has(service.name) && (
                              <span
                                className="badge"
                                style={{
                                  backgroundColor: '#007bff',
                                  color: '#fff',
                                  fontSize: '0.7rem',
                                  padding: '4px 8px',
                                  borderRadius: '4px'
                                }}>
                                RECOMMENDED
                              </span>
                            )}
                          {service.isRecommended &&
                            addedRecommendedServices.has(service.name) && (
                              <span
                                className="badge"
                                style={{
                                  backgroundColor: '#28a745',
                                  color: '#fff',
                                  fontSize: '0.7rem',
                                  padding: '4px 8px',
                                  borderRadius: '4px'
                                }}>
                                ADDED
                              </span>
                            )}
                        </div>
                        {service.description ? (
                          <p className="service-description">
                            {service.description}
                          </p>
                        ) : (
                          <p
                            className="service-description"
                            style={{ fontStyle: 'italic', color: '#adb5bd' }}>
                            No description available
                          </p>
                        )}

                        {/* Indicador de loading individual para cada servicio - DENTRO del box */}
                        {loadingAgent && (
                          <div style={{ marginTop: 12 }}>
                            <div
                              className="d-flex align-items-center"
                              style={{ minHeight: 40 }}>
                              <div
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-label="Loading agent"
                              />
                              <span>Analyzing {service.name}...</span>
                            </div>
                          </div>
                        )}

                        {/* Notificaciones individuales para cada servicio - DENTRO del box */}
                        {showQualityAgent && !loadingAgent && qualityAgentData && (
                          <div style={{ marginTop: 12 }}>
                            {(() => {
                              // PRIMERO: Verificar si es un servicio recomendado (independientemente de si tiene descripción)
                              if (service.isRecommended) {
                                // Si ya fue añadido, no mostrar notificación
                                if (
                                  addedRecommendedServices.has(service.name)
                                ) {
                                  return null;
                                }

                                return !dismissedNotifications[
                                  `service_${service.name}_recommended`
                                ] ? (
                                  <QualityAgentNotification
                                    status="recommended"
                                    message={`Recommended service: ${service.name}`}
                                    onFix={async () => {
                                      // Usar el ID que ya se asignó al servicio cuando se parseó
                                      const serviceId = service.id;
                                      const fieldPath = `serviceItems.recommendedServices.${serviceId}`;

                                      // Llamar a handleFixField con el payload específico
                                      await handleFixField(fieldPath, {
                                        serviceId
                                      });

                                      // Marcar como añadido inmediatamente después del fix
                                      setAddedRecommendedServices(
                                        (prev) =>
                                          new Set([...prev, service.name])
                                      );
                                    }}
                                    onDismiss={() =>
                                      handleDismissNotification(
                                        `service_${service.name}_recommended`
                                      )
                                    }
                                    suggestion="Add this recommended service to improve your business offerings."
                                    loadingFix={
                                      loadingFix['recommended_services_general']
                                    }
                                    loadingDismiss={
                                      loadingDismiss[
                                        `service_${service.name}_recommended`
                                      ]
                                    }
                                    isAnyOperationInProgress={
                                      isAnyOperationInProgress
                                    }
                                  />
                                ) : null;
                              }

                              // SEGUNDO: Para servicios existentes (no recomendados)
                              const hasDescription =
                                service.description &&
                                service.description.trim() !== '';

                              if (hasDescription) {
                                // Servicio existente con descripción - notificación verde (solo si no está dismissada)
                                return !dismissedNotifications[
                                  `service_${service.name}_good`
                                ] ? (
                                  <QualityAgentNotification
                                    status="good"
                                    message={`${service.name} description is OK.`}
                                    onDismiss={() =>
                                      handleDismissNotification(
                                        `service_${service.name}_good`
                                      )
                                    }
                                    loadingDismiss={
                                      loadingDismiss[
                                        `service_${service.name}_good`
                                      ]
                                    }
                                  />
                                ) : null;
                              } else {
                                // Servicio existente sin descripción - notificación roja con sugerencia (solo si no está dismissada)
                                if (
                                  dismissedNotifications[
                                    `service_${service.name}_bad`
                                  ]
                                ) {
                                  return null;
                                }

                                const getServiceSpecificSuggestion = (
                                  serviceName
                                ) => {
                                  try {
                                    const serviceSuggestionData =
                                      qualityAgentData?.recomendations
                                        ?.serviceItems
                                        ?.descriptionKeywordAnalysis?.message;
                                    if (serviceSuggestionData) {
                                      const parsedSuggestions = JSON.parse(
                                        serviceSuggestionData
                                      );
                                      if (Array.isArray(parsedSuggestions)) {
                                        // Buscar la sugerencia específica para este servicio
                                        const specificSuggestion =
                                          parsedSuggestions.find(
                                            (suggestion) => {
                                              // Verificar freeFormServiceItem
                                              if (
                                                suggestion.freeFormServiceItem
                                                  ?.label?.displayName
                                              ) {
                                                return (
                                                  suggestion.freeFormServiceItem.label.displayName.toLowerCase() ===
                                                  serviceName.toLowerCase()
                                                );
                                              }
                                              // Verificar structuredServiceItem por displayName (nueva estructura)
                                              if (
                                                suggestion.structuredServiceItem
                                                  ?.label?.displayName
                                              ) {
                                                return (
                                                  suggestion.structuredServiceItem.label.displayName.toLowerCase() ===
                                                  serviceName.toLowerCase()
                                                );
                                              }
                                              // Verificar structuredServiceItem por serviceTypeId (estructura legacy)
                                              if (
                                                suggestion.structuredServiceItem
                                                  ?.serviceTypeId
                                              ) {
                                                const serviceTypeName =
                                                  suggestion.structuredServiceItem.serviceTypeId
                                                    .replace('job_type_id:', '')
                                                    .replace(/_/g, ' ')
                                                    .split(' ')
                                                    .map(
                                                      (word) =>
                                                        word
                                                          .charAt(0)
                                                          .toUpperCase() +
                                                        word.slice(1)
                                                    )
                                                    .join(' ');
                                                return (
                                                  serviceTypeName.toLowerCase() ===
                                                  serviceName.toLowerCase()
                                                );
                                              }
                                              return false;
                                            }
                                          );

                                        if (specificSuggestion) {
                                          // Priorizar freeFormServiceItem description
                                          if (
                                            specificSuggestion
                                              .freeFormServiceItem?.label
                                              ?.description
                                          ) {
                                            return specificSuggestion
                                              .freeFormServiceItem.label
                                              .description;
                                          }
                                          // Usar structuredServiceItem description - nueva estructura
                                          if (
                                            specificSuggestion
                                              .structuredServiceItem?.label
                                              ?.description
                                          ) {
                                            return specificSuggestion
                                              .structuredServiceItem.label
                                              .description;
                                          }
                                          // Usar structuredServiceItem description - estructura legacy
                                          if (
                                            specificSuggestion
                                              .structuredServiceItem
                                              ?.description
                                          ) {
                                            return specificSuggestion
                                              .structuredServiceItem
                                              .description;
                                          }
                                        }
                                      }
                                    }
                                  } catch (error) {
                                    console.error(
                                      'Error parsing service suggestions:',
                                      error
                                    );
                                  }

                                  // Fallback: sugerencia genérica
                                  return `Add a detailed description for ${serviceName} to help customers understand what this service includes.`;
                                };

                                const serviceSpecificSuggestion =
                                  getServiceSpecificSuggestion(service.name);

                                return (
                                  <QualityAgentNotification
                                    status="bad"
                                    message={`${service.name} needs a description.`}
                                    onFix={async () => {
                                      // Obtener servicios actuales (sin servicios recomendados) para el índice correcto
                                      const servicesData = findServicesField(
                                        googleProfileInfo.data
                                      );
                                      const currentServices =
                                        processServicesList(servicesData) || [];

                                      // Generar un fieldPath específico para este servicio individual
                                      const serviceIndex =
                                        currentServices.findIndex(
                                          (s) => s.name === service.name
                                        );

                                      if (serviceIndex === -1) {
                                        console.error(
                                          `Service ${service.name} not found in current services`
                                        );
                                        return;
                                      }

                                      const fieldPath = `serviceItems.${serviceIndex}.description`;

                                      await handleFixField(fieldPath, {
                                        serviceName: service.name,
                                        serviceIndex: serviceIndex
                                      });
                                    }}
                                    onDismiss={() =>
                                      handleDismissNotification(
                                        `service_${service.name}_bad`
                                      )
                                    }
                                    suggestion={serviceSpecificSuggestion}
                                    loadingFix={
                                      loadingFix[
                                        `service_${service.name}_individual`
                                      ]
                                    }
                                    loadingDismiss={
                                      loadingDismiss[
                                        `service_${service.name}_bad`
                                      ]
                                    }
                                    isAnyOperationInProgress={
                                      isAnyOperationInProgress
                                    }
                                  />
                                );
                              }
                            })()}
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            );
          })}

          {/* Notificaciones del Quality Agent para servicios */}
          {showQualityAgent && !loadingAgent && qualityAgentData && (
            <>
              {/* Notificación general para servicios sin descripción */}
              {(() => {
                const servicesWithoutDescription = allServices.filter(
                  (service) =>
                    !service.description || service.description.trim() === ''
                );
                const hasServicesWithoutDescription =
                  servicesWithoutDescription.length > 0;

                return hasServicesWithoutDescription &&
                  !dismissedNotifications['services_general'] ? (
                  <div style={{ marginTop: 16 }}>
                    <QualityAgentNotification
                      status="bad"
                      message={`${servicesWithoutDescription.length} of your services are missing descriptions.`}
                      onFix={() =>
                        handleFixField(
                          'serviceItems.descriptionKeywordAnalysis'
                        )
                      }
                      onDismiss={() =>
                        handleDismissNotification('services_general')
                      }
                      suggestion={
                        qualityAgentData?.recomendations?.serviceItems
                          ?.descriptionKeywordAnalysis?.message
                          ? 'Click Fix to automatically add optimized descriptions to your services based on AI recommendations.'
                          : 'Add detailed descriptions to all your services to improve visibility and attract more customers.'
                      }
                      loadingFix={loadingFix['services_general']}
                      loadingDismiss={loadingDismiss['services_general']}
                      isAnyOperationInProgress={isAnyOperationInProgress}
                    />
                  </div>
                ) : null;
              })()}

              {/* Notificación general para servicios recomendados */}
              {qualityAgentData?.recomendations?.serviceItems
                ?.recommendedServices?.message &&
                !dismissedNotifications['recommended_services_general'] &&
                // Verificar si algún servicio recomendado individual ya fue añadido
                addedRecommendedServices.size === 0 &&
                // Verificar si algún servicio recomendado individual ha sido dismissado
                !(() => {
                  if (
                    !recommendedServicesList ||
                    recommendedServicesList.length === 0
                  )
                    return false;
                  return recommendedServicesList.some(
                    (service) =>
                      dismissedNotifications[
                        `service_${service.name}_recommended`
                      ]
                  );
                })() && (
                  <div style={{ marginTop: 16 }}>
                    <QualityAgentNotification
                      status="recommended"
                      message={`We have ${
                        recommendedServicesList
                          ? recommendedServicesList.length
                          : 0
                      } additional service recommendations for your business.`}
                      onFix={
                        recommendedServicesList &&
                        recommendedServicesList.length > 0
                          ? () =>
                              handleFixField('serviceItems.recommendedServices')
                          : null
                      }
                      onDismiss={() =>
                        handleDismissNotification(
                          'recommended_services_general'
                        )
                      }
                      suggestion={
                        recommendedServicesList &&
                        recommendedServicesList.length > 0
                          ? 'Click Fix All to add all recommended services to your business profile and expand your service offerings.'
                          : 'No additional service recommendations available at this time.'
                      }
                      loadingFix={loadingFix['recommended_services_general']}
                      loadingDismiss={
                        loadingDismiss['recommended_services_general']
                      }
                      fixButtonText="Fix All"
                      isAnyOperationInProgress={isAnyOperationInProgress}
                    />
                  </div>
                )}
            </>
          )}
        </div>
      );
    };

    // Función para calcular el porcentaje de optimización del perfil
    const calculateOptimizationPercentage = () => {
      if (!googleProfileInfo.data) return 0;

      const fields = [];
      const totalFields = 7; // Total de condiciones a evaluar
      const conditionsStatus = {}; // Para trackear el estado de cada condición

      // 1. Description (considerar fixedDescription si está disponible)
      const description =
        fixedDescription !== null
          ? fixedDescription
          : googleProfileInfo.data?.profile?.description;
      const hasDescription = description && description.trim() !== '';
      conditionsStatus['1. Description'] = {
        fulfilled: hasDescription,
        value: description ? description.substring(0, 50) + '...' : 'N/A'
      };
      if (hasDescription) {
        fields.push('description');
      }

      // 2. Service Area (considerar fixedServiceArea si está disponible)
      const serviceArea =
        fixedServiceArea !== null
          ? fixedServiceArea
          : googleProfileInfo.data?.serviceArea?.places?.placeInfos;
      const hasServiceArea = serviceArea && serviceArea.length > 0;
      conditionsStatus['2. Service Area'] = {
        fulfilled: hasServiceArea,
        value: hasServiceArea ? `${serviceArea.length} areas` : 'No areas'
      };
      if (hasServiceArea) {
        fields.push('serviceArea');
      }

      // 3. Review Frequency
      const reviewFrecuency =
        googleProfileInfo.data?.reviews_info?.reviewFrecuency;
      const hasReviewFrequency =
        reviewFrecuency && parseFloat(reviewFrecuency) >= 1;
      conditionsStatus['3. Review Frequency'] = {
        fulfilled: hasReviewFrequency,
        value: reviewFrecuency ? `${reviewFrecuency} (need >= 1)` : 'N/A'
      };
      if (hasReviewFrequency) {
        fields.push('reviewFrequency');
      }

      // 4. Google Rating
      const averageRating = googleProfileInfo.data?.reviews_info?.averageRating;
      const hasGoodRating = averageRating && parseFloat(averageRating) >= 4;
      conditionsStatus['4. Google Rating'] = {
        fulfilled: hasGoodRating,
        value: averageRating ? `${averageRating} (need >= 4)` : 'N/A'
      };
      if (hasGoodRating) {
        fields.push('googleRating');
      }

      // 5. Service List (servicios > 0 y cada servicio debe tener descripción)
      const servicesData = findServicesField(googleProfileInfo.data);
      const currentServices = processServicesList(servicesData);
      // Usar fixedServicesList si está disponible
      const servicesToCheck =
        fixedServicesList !== null ? fixedServicesList : currentServices;
      let hasValidServices = false;
      if (servicesToCheck && servicesToCheck.length > 0) {
        const allServicesHaveDescription = servicesToCheck.every(
          (service) => service.description && service.description.trim() !== ''
        );
        hasValidServices = allServicesHaveDescription;
        conditionsStatus['5. Service List'] = {
          fulfilled: hasValidServices,
          value: `${servicesToCheck.length} services, all with description: ${allServicesHaveDescription}`
        };
        if (allServicesHaveDescription) {
          fields.push('serviceList');
        }
      } else {
        conditionsStatus['5. Service List'] = {
          fulfilled: false,
          value: 'No services'
        };
      }

      // 6. Review Replies
      const reviewsReplied =
        googleProfileInfo.data?.reviews_info?.reviewsReplied;
      const hasReviewReplies =
        reviewsReplied && parseFloat(reviewsReplied) >= 1;
      conditionsStatus['6. Review Replies'] = {
        fulfilled: hasReviewReplies,
        value: reviewsReplied ? `${reviewsReplied} (need >= 1)` : 'N/A'
      };
      if (hasReviewReplies) {
        fields.push('reviewReplies');
      }

      // 7. Post Keyword Optimization (considerar fixedPostsKeywords si está disponible)
      const postsKeywords =
        fixedPostsKeywords !== null
          ? fixedPostsKeywords
          : googleProfileInfo.data?.posts_info?.posts_keywords || '';
      const hasPostsKeywords = postsKeywords && postsKeywords.trim() !== '';

      conditionsStatus['7. Post Keywords'] = {
        fulfilled: hasPostsKeywords,
        value: hasPostsKeywords
          ? `"${postsKeywords.substring(0, 50)}..."`
          : 'No keywords set',
        debug: {
          fixedPostsKeywords,
          actualValue: googleProfileInfo.data?.posts_info?.posts_keywords,
          usedValue: postsKeywords
        }
      };
      if (hasPostsKeywords) {
        fields.push('postKeywordOptimization');
      }

      // Log detallado del estado de cada condición
      console.log('=== OPTIMIZATION CONDITIONS STATUS ===');
      console.log(
        `Total: ${fields.length}/${totalFields} conditions fulfilled`
      );
      Object.entries(conditionsStatus).forEach(([condition, status]) => {
        console.log(
          `${status.fulfilled ? '✅' : '❌'} ${condition}: ${status.value}`
        );
        if (status.debug) {
          // console.log('  Debug:', status.debug);
        }
      });
      console.log('=======================================');

      return Math.round((fields.length / totalFields) * 100) || 0;
    };

    // Calcular el porcentaje de optimización
    const calculatedOptimizationPercentage = calculateOptimizationPercentage();

    return (
      <div style={{ position: 'inherit' }}>
        {googleProfileInfo.loading ? (
          <div className="d-flex justify-content-center">
            <div className="spinner-border spinner-border-sm">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        ) : (
          <div
            style={{
              width: '100%',
              height: '100vh',
              paddingTop: '20px',
              position: 'relative'
            }}>
            {/* Profile Optimization Rating - Posicionado en la esquina superior derecha */}
            <div
              style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                zIndex: 10,
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                padding: '8px',
                marginRight: '20px'
              }}>
              <ProfileOptimizationRating
                percentage={calculatedOptimizationPercentage}
                size="small"
                showLabel={true}
              />
            </div>

            {/* Mensajes de error */}
            {saveMessage.show && saveMessage.type === 'error' && (
              <div
                style={{
                  position: 'absolute',
                  top: '80px',
                  right: '20px',
                  zIndex: 10,
                  backgroundColor: '#f8d7da',
                  color: '#721c24',
                  border: '1px solid #f5c6cb',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  maxWidth: '300px'
                }}>
                <div className="d-flex align-items-center">
                  <i className="fas fa-exclamation-circle me-2"></i>
                  <span>{saveMessage.message}</span>
                </div>
              </div>
            )}
            <nav
              className="nav nav-pills"
              style={{
                boxSizing: 'border-box',
                paddingLeft: '10px',
                backgroundColor: 'white'
              }}>
              <a
                href="#about"
                onClick={() => setCurrSection('about')}
                className={`nav-link ${
                  currSection === 'about' ? 'active' : ''
                }`}>
                About
              </a>
              <a
                href="#location"
                onClick={() => setCurrSection('location')}
                className={`nav-link ${
                  currSection === 'location' ? 'active' : ''
                }`}>
                Location
              </a>
              <a
                href="#contact"
                onClick={() => setCurrSection('contact')}
                className={`nav-link ${
                  currSection === 'contact' ? 'active' : ''
                }`}>
                Contact
              </a>

              <a
                href="#hours"
                onClick={() => setCurrSection('hours')}
                className={`nav-link ${
                  currSection === 'hours' ? 'active' : ''
                }`}>
                Hours
              </a>
              <a
                href="#more"
                onClick={() => setCurrSection('more')}
                className={`nav-link ${
                  currSection === 'more' ? 'active' : ''
                }`}>
                More
              </a>
            </nav>
            <div
              id="scrollCard"
              style={{
                marginTop: '10px',
                overflowY: 'scroll',
                overflowX: 'hidden',
                height: isEditing ? '85vh' : '90vh' // Usar viewport height para aprovechar el 100% del espacio vertical disponible
              }}>
              <div id="about" className="pd-25" style={{ minHeight: '300px' }}>
                <div className="title">
                  <div>
                    <h5 style={{ marginBottom: '0.9rem' }}>
                      About your business
                    </h5>
                  </div>
                </div>
                <InfoField
                  title={'Business name'}
                  isEditing={isEditing}
                  canEdit={!GBPfields?.businessName}
                  hasChanges={!!editedFields.businessName}
                  onFieldChange={(value) =>
                    handleFieldChange('businessName', value)
                  }>
                  {googleProfileInfo.data?.title ? (
                    googleProfileInfo.data?.title
                  ) : (
                    <i className="fas fa-minus"></i>
                  )}
                </InfoField>
                <hr
                  style={{
                    margin: '10px 0',
                    border: 'none',
                    borderTop: '1px solid #e0e0e0'
                  }}
                />
                <Categories
                  title={'Business category'}
                  isEditing={isEditing}
                  canEdit={!GBPfields.businessCategory}
                  categories={categories}
                  googleProfileInfo={
                    googleProfileInfo.data?.categories?.primaryCategory
                      ?.displayName
                  }
                  additionalCategories={
                    googleProfileInfo.data?.categories?.additionalCategories
                  }
                  onFieldChange={(value) =>
                    handleFieldChange('businessCategory', value)
                  }>
                  {googleProfileInfo.data?.categories?.primaryCategory
                    ?.displayName ? (
                    googleProfileInfo.data?.categories.primaryCategory
                      .displayName
                  ) : (
                    <i className="fas fa-minus"></i>
                  )}
                </Categories>

                {/* Notificaciones individuales para categorías recomendadas */}
                {showQualityAgent && (
                  <div style={{ marginTop: 8 }}>
                    {loadingAgent ? (
                      <div
                        className="d-flex align-items-center"
                        style={{ minHeight: 60 }}>
                        <div
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-label="Loading agent"
                        />
                        <span>Analyzing categories...</span>
                      </div>
                    ) : (
                      recommendedCategories &&
                      recommendedCategories.length > 0 &&
                      recommendedCategories.map((category, index) => (
                        <div
                          key={category.id || index}
                          style={{ marginBottom: 8 }}>
                          <QualityAgentNotification
                            status="recommended"
                            message={category.display_name}
                            onFix={() => handleFixIndividualCategory(category)}
                            onDismiss={() =>
                              handleDismissIndividualCategory(category.id)
                            }
                            suggestion={`This category would help customers find your business when searching for "${category.display_name.toLowerCase()}" services.`}
                            loadingFix={loadingFix[`category_${category.id}`]}
                            loadingDismiss={
                              loadingDismiss[`category_${category.id}`]
                            }
                            fixButtonText="Add"
                            isAnyOperationInProgress={isAnyOperationInProgress}
                          />
                        </div>
                      ))
                    )}
                  </div>
                )}

                <hr
                  style={{
                    margin: '10px 0',
                    border: 'none',
                    borderTop: '1px solid #e0e0e0'
                  }}
                />
                <InfoField
                  title={'Description'}
                  isEditing={isEditing}
                  canEdit={!GBPfields.description}
                  hasChanges={!!editedFields.description}
                  onFieldChange={(value) =>
                    handleFieldChange('description', value)
                  }>
                  {visualDescription ? (
                    visualDescription
                  ) : (
                    <i className="fas fa-minus"></i>
                  )}
                </InfoField>
                {showQualityAgent && !dismissedNotifications.description && (
                  <div style={{ marginTop: 8 }}>
                    {loadingAgent ? (
                      <div
                        className="d-flex align-items-center"
                        style={{ minHeight: 60 }}>
                        <div
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-label="Loading agent"
                        />
                        <span>Analyzing description...</span>
                      </div>
                    ) : qualityAgentData ? (
                      <QualityAgentNotification
                        status={isDescriptionGood ? 'good' : 'bad'}
                        message={message}
                        onFix={
                          !isDescriptionGood
                            ? () => handleFixField('profile.description')
                            : undefined
                        }
                        onDismiss={() =>
                          handleDismissNotification('description')
                        }
                        suggestion={!isDescriptionGood ? suggestion : undefined}
                        loadingFix={loadingFix.description}
                        loadingDismiss={loadingDismiss.description}
                      />
                    ) : description ? (
                      <QualityAgentNotification
                        status="good"
                        message="Your description is high quality!"
                        onDismiss={() =>
                          handleDismissNotification('description')
                        }
                        loadingDismiss={loadingDismiss.description}
                      />
                    ) : null}
                  </div>
                )}
                {/* --- INICIO CAMPOS DE CALIDAD --- */}
                <hr
                  style={{
                    margin: '10px 0',
                    border: 'none',
                    borderTop: '1px solid #e0e0e0'
                  }}
                />
              </div>
              {/* Service Area con notificación del agente */}
              <div
                id="location"
                className="pd-25"
                style={{ minHeight: '300px' }}>
                <div className="title">
                  <div>
                    <h5 style={{ marginBottom: '0.9rem' }}>
                      Location and areas
                    </h5>
                  </div>
                </div>
                {fixedServiceArea !== null ? (
                  // Renderizado directo cuando hay fixedServiceArea
                  <div className="info-field">
                    <div>
                      <div>
                        <strong>Service area:</strong>
                      </div>
                    </div>
                    <div style={{ marginTop: '14px' }}>
                      <div>
                        <span>
                          {(() => {
                            // Obtener las áreas de servicio originales
                            const originalServiceAreas =
                              googleProfileInfo.data?.serviceArea?.places
                                ?.placeInfos || [];
                            const originalPlaceNames = originalServiceAreas.map(
                              (place) => place.placeName
                            );

                            // Obtener las recomendaciones
                            const recommendedPlaceNames = fixedServiceArea.map(
                              (place) => place.placeName || place.name
                            );

                            // Combinar originales + recomendaciones, evitando duplicados
                            const combinedPlaceNames = [...originalPlaceNames];

                            recommendedPlaceNames.forEach((placeName) => {
                              if (!combinedPlaceNames.includes(placeName)) {
                                combinedPlaceNames.push(placeName);
                              }
                            });

                            // console.log('Combined service areas:', {
                            //   original: originalPlaceNames,
                            //   recommended: recommendedPlaceNames,
                            //   combined: combinedPlaceNames
                            // });

                            return combinedPlaceNames.map(
                              (placeName, index) => (
                                <span key={`${index}-${forceRender}`}>
                                  {placeName}
                                  <br />
                                </span>
                              )
                            );
                          })()}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  // Renderizado normal con ServiceArea cuando no hay fixedServiceArea
                  <ServiceArea
                    title={'Service area'}
                    isEditing={isEditing}
                    canEdit={false}
                    googleProfileInfo={
                      googleProfileInfo.data?.serviceArea?.places?.placeInfos
                    }
                    onFieldChange={(value) =>
                      handleFieldChange('serviceArea', value)
                    }>
                    {googleProfileInfo.data?.serviceArea?.places?.placeInfos ? (
                      googleProfileInfo.data?.serviceArea?.places?.placeInfos.map(
                        (place, index) => (
                          <span key={index}>
                            {place.placeName}
                            <br />
                          </span>
                        )
                      )
                    ) : (
                      <i className="fas fa-minus"></i>
                    )}
                  </ServiceArea>
                )}
                {showQualityAgent && (
                  <div style={{ marginTop: 8 }}>
                    {loadingAgent ? (
                      <div
                        className="d-flex align-items-center"
                        style={{ minHeight: 60 }}>
                        <div
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-label="Loading agent"
                        />
                        <span>Analyzing service areas...</span>
                      </div>
                    ) : (
                      (() => {
                        const currentServiceAreas =
                          googleProfileInfo.data?.serviceArea?.places
                            ?.placeInfos || [];

                        // Si ya tiene 12 o más, mostrar notificación verde
                        if (currentServiceAreas.length >= 12) {
                          if (!dismissedNotifications.serviceArea_max) {
                            return (
                              <QualityAgentNotification
                                status="good"
                                message="Service areas are complete."
                                onDismiss={() =>
                                  handleDismissNotification('serviceArea_max')
                                }
                                loadingDismiss={loadingDismiss.serviceArea_max}
                              />
                            );
                          }
                          return null;
                        }

                        // Si tiene menos de 12, mostrar recomendaciones individuales
                        if (
                          recommendedServiceAreas &&
                          recommendedServiceAreas.length > 0
                        ) {
                          return recommendedServiceAreas.map((place) => {
                            // Verificar si ya fue añadida
                            if (
                              addedRecommendedServiceAreas.has(place.placeName)
                            ) {
                              return null;
                            }

                            const notificationKey = `recommended_service_area_${place.id}`;
                            if (dismissedNotifications[notificationKey]) {
                              return null;
                            }

                            return (
                              <div key={place.id} style={{ marginBottom: 8 }}>
                                <QualityAgentNotification
                                  status="recommended"
                                  message={`Add ${place.placeName} to your service area`}
                                  onFix={async () => {
                                    await handleFixRecommendedServiceArea(
                                      place.id,
                                      place.placeName
                                    );
                                  }}
                                  onDismiss={() =>
                                    handleDismissNotification(notificationKey)
                                  }
                                  suggestion={`Expanding your service area to ${place.placeName} will help customers in this location find your business.`}
                                  loadingFix={
                                    loadingFix[
                                      `recommended_service_area_${place.id}`
                                    ]
                                  }
                                  loadingDismiss={
                                    loadingDismiss[
                                      `recommended_service_area_${place.id}`
                                    ]
                                  }
                                  fixButtonText="Add"
                                  isAnyOperationInProgress={
                                    isAnyOperationInProgress
                                  }
                                />
                              </div>
                            );
                          });
                        }
                        return null;
                      })()
                    )}
                  </div>
                )}
                <hr
                  style={{
                    margin: '10px 0',
                    border: 'none',
                    borderTop: '1px solid #e0e0e0'
                  }}
                />
                {/* Review Frequency */}
                <InfoField
                  title={'Review Frequency'}
                  isEditing={isEditing}
                  canEdit={!GBPfields.reviewFrequency}
                  hasChanges={!!editedFields.reviewFrequency}
                  onFieldChange={(value) =>
                    handleFieldChange('reviewFrequency', value)
                  }>
                  {googleProfileInfo.data?.reviews_info?.reviewFrecuency ? (
                    googleProfileInfo.data?.reviews_info?.reviewFrecuency
                  ) : (
                    <i className="fas fa-minus"></i>
                  )}
                </InfoField>
                {loadingAgent && (
                  <div style={{ marginTop: 8 }}>
                    <div
                      className="d-flex align-items-center"
                      style={{ minHeight: 60 }}>
                      <div
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-label="Loading agent"
                      />
                      <span>Analyzing review frequency...</span>
                    </div>
                  </div>
                )}
                {showAgentNotifications &&
                  !dismissedNotifications.reviewFrequency && (
                    <div style={{ marginTop: 8 }}>
                      {(() => {
                        // Lógica condicional basada en review frequency > 1
                        const reviewFreqValue = parseFloat(
                          googleProfileInfo.data?.reviews_info?.reviewFrecuency
                        );
                        const isReviewFreqGood = reviewFreqValue > 1;

                        const mockStatus = isReviewFreqGood ? 'good' : 'bad';
                        const mockMessage = isReviewFreqGood
                          ? 'The review frequency is OK.'
                          : 'Review Frequency should be more than 1 review per week.';
                        const mockSuggestion = isReviewFreqGood
                          ? 'Maintain this review frequency to improve your visibility.'
                          : 'Encourage your clients to leave reviews to increase frequency.';

                        return (
                          <QualityAgentNotification
                            status={mockStatus}
                            message={mockMessage}
                            onDismiss={() =>
                              handleDismissNotification('reviewFrequency')
                            }
                            suggestion={mockSuggestion}
                            loadingFix={loadingFix.reviewFrequency}
                            loadingDismiss={loadingDismiss.reviewFrequency}
                          />
                        );
                      })()}
                    </div>
                  )}
                <hr
                  style={{
                    margin: '10px 0',
                    border: 'none',
                    borderTop: '1px solid #e0e0e0'
                  }}
                />
                {/* Google Rating */}
                <InfoField
                  title={'Google Rating'}
                  isEditing={isEditing}
                  canEdit={!GBPfields.googleRating}
                  hasChanges={!!editedFields.googleRating}
                  onFieldChange={(value) =>
                    handleFieldChange('googleRating', value)
                  }>
                  {googleProfileInfo.data?.reviews_info?.averageRating ? (
                    googleProfileInfo.data?.reviews_info?.averageRating.toFixed(
                      1
                    )
                  ) : (
                    <i className="fas fa-minus"></i>
                  )}
                </InfoField>
                {loadingAgent && (
                  <div style={{ marginTop: 8 }}>
                    <div
                      className="d-flex align-items-center"
                      style={{ minHeight: 60 }}>
                      <div
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-label="Loading agent"
                      />
                      <span>Analyzing Google rating...</span>
                    </div>
                  </div>
                )}
                {showAgentNotifications &&
                  !dismissedNotifications.googleRating && (
                    <div style={{ marginTop: 8 }}>
                      {(() => {
                        // Lógica condicional basada en Google Rating > 4
                        const ratingValue = parseFloat(
                          googleProfileInfo.data?.reviews_info?.averageRating
                        );
                        const isRatingGood = ratingValue > 4;

                        const mockStatus = isRatingGood ? 'good' : 'bad';
                        const mockMessage = isRatingGood
                          ? 'The Google rating is OK.'
                          : 'Google Rating should be greater than 4.';
                        const mockSuggestion = isRatingGood
                          ? 'Maintain this rating to build trust with potential clients.'
                          : 'Ask satisfied clients to leave positive reviews to improve your rating.';

                        return (
                          <QualityAgentNotification
                            status={mockStatus}
                            message={mockMessage}
                            onDismiss={() =>
                              handleDismissNotification('googleRating')
                            }
                            suggestion={mockSuggestion}
                            loadingFix={loadingFix.googleRating}
                            loadingDismiss={loadingDismiss.googleRating}
                          />
                        );
                      })()}
                    </div>
                  )}
                <hr
                  style={{
                    margin: '10px 0',
                    border: 'none',
                    borderTop: '1px solid #e0e0e0'
                  }}
                />
                <InfoField
                  title={'Services List'}
                  isEditing={isEditing}
                  canEdit={false}
                  onFieldChange={(value) =>
                    handleFieldChange('servicesList', value)
                  }>
                  {(() => {
                    // Buscar el campo de servicios automáticamente
                    const servicesData = findServicesField(
                      googleProfileInfo.data
                    );
                    // console.log('Services Data found:', servicesData);

                    const currentServices = processServicesList(servicesData);
                    // console.log('Current services processed:', currentServices);

                    // Obtener servicios recomendados si están disponibles
                    let recommendedServicesList = [];
                    if (
                      qualityAgentData?.recomendations?.serviceItems
                        ?.recommendedServices?.message
                    ) {
                      // console.log(
                      //   'Raw recommended services message:',
                      //   qualityAgentData.recomendations.serviceItems
                      //     .recommendedServices.message
                      // );

                      try {
                        recommendedServicesList = parseRecommendedServices(
                          qualityAgentData.recomendations.serviceItems
                            .recommendedServices.message
                        );
                        // console.log(
                        //   'Parsed recommended services:',
                        //   recommendedServicesList
                        // );
                        // No llamar setRecommendedServices aquí para evitar re-renders infinitos
                      } catch (error) {
                        console.error(
                          'Error parsing recommended services:',
                          error
                        );
                      }
                    }

                    // Combinar servicios existentes con servicios recomendados
                    let allServices = [];

                    // Si hay servicios fijos, usar esos
                    if (fixedServicesList !== null) {
                      // console.log(
                      //   'Using fixed services list:',
                      //   fixedServicesList
                      // );
                      allServices = [...fixedServicesList];
                    } else if (currentServices) {
                      // console.log('Using current services:', currentServices);
                      allServices = [...currentServices];
                    }

                    // Agregar servicios recomendados que no estén ya en la lista
                    if (recommendedServicesList.length > 0) {
                      // console.log('Adding recommended services to list');
                      recommendedServicesList.forEach((recommendedService) => {
                        const alreadyExists = allServices.some(
                          (existingService) =>
                            existingService.name === recommendedService.name
                        );
                        if (!alreadyExists) {
                          allServices.push(recommendedService);
                        }
                      });
                    }

                    // console.log('All services (final):', allServices);

                    return renderServicesList(servicesData, allServices);
                  })()}
                </InfoField>
                {loadingAgent && (
                  <div style={{ marginTop: 8 }}>
                    <div
                      className="d-flex align-items-center"
                      style={{ minHeight: 60 }}>
                      <div
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-label="Loading agent"
                      />
                      <span>
                        Analyzing services and generating recommendations...
                      </span>
                    </div>
                  </div>
                )}
                <hr
                  style={{
                    margin: '10px 0',
                    border: 'none',
                    borderTop: '1px solid #e0e0e0'
                  }}
                />
                {/* Review Replies */}
                <InfoField
                  title={'Review Replies'}
                  isEditing={isEditing}
                  canEdit={!GBPfields.reviewReplies}
                  hasChanges={!!editedFields.reviewReplies}
                  onFieldChange={(value) =>
                    handleFieldChange('reviewReplies', value)
                  }>
                  {googleProfileInfo.data?.reviews_info?.reviewsReplied ? (
                    String(googleProfileInfo.data?.reviews_info?.reviewsReplied)
                  ) : (
                    <i className="fas fa-minus"></i>
                  )}
                </InfoField>
                {loadingAgent && (
                  <div style={{ marginTop: 8 }}>
                    <div
                      className="d-flex align-items-center"
                      style={{ minHeight: 60 }}>
                      <div
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-label="Loading agent"
                      />
                      <span>Analyzing review replies...</span>
                    </div>
                  </div>
                )}
                {showAgentNotifications &&
                  !dismissedNotifications.reviewReplies && (
                    <div style={{ marginTop: 8 }}>
                      {(() => {
                        // Lógica condicional basada en Review Replies > 1
                        const repliesValue = parseFloat(
                          googleProfileInfo.data?.reviews_info?.reviewsReplied
                        );
                        const isRepliesGood = repliesValue > 1;

                        const mockStatus = isRepliesGood ? 'good' : 'bad';
                        const mockMessage = isRepliesGood
                          ? 'The review replies are OK.'
                          : 'Review Replies should more than 1 reply per week.';
                        const mockSuggestion = isRepliesGood
                          ? 'Keep responding to reviews to maintain good engagement.'
                          : 'Make sure to reply to all reviews to show customer care.';

                        return (
                          <QualityAgentNotification
                            status={mockStatus}
                            message={mockMessage}
                            onFix={
                              !isRepliesGood
                                ? () =>
                                    handleFixField('review_info.reviewsReplied')
                                : undefined
                            }
                            onDismiss={() =>
                              handleDismissNotification('reviewReplies')
                            }
                            suggestion={mockSuggestion}
                            loadingFix={loadingFix.reviewReplies}
                            loadingDismiss={loadingDismiss.reviewReplies}
                          />
                        );
                      })()}
                    </div>
                  )}
                <hr
                  style={{
                    margin: '10px 0',
                    border: 'none',
                    borderTop: '1px solid #e0e0e0'
                  }}
                />
                {/* # Of Images */}
                <InfoField
                  title={'Number Of Images'}
                  isEditing={isEditing}
                  canEdit={!GBPfields.numberOfImages}
                  hasChanges={!!editedFields.numberOfImages}
                  onFieldChange={(value) =>
                    handleFieldChange('numberOfImages', value)
                  }>
                  {googleProfileInfo.data?.images_info?.totalMediaItemCount ? (
                    googleProfileInfo.data?.images_info?.totalMediaItemCount
                  ) : (
                    <i className="fas fa-minus"></i>
                  )}
                </InfoField>
                {loadingAgent && (
                  <div style={{ marginTop: 8 }}>
                    <div
                      className="d-flex align-items-center"
                      style={{ minHeight: 60 }}>
                      <div
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-label="Loading agent"
                      />
                      <span>Analyzing number of images...</span>
                    </div>
                  </div>
                )}
                {showAgentNotifications &&
                  !dismissedNotifications.numberOfImages && (
                    <div style={{ marginTop: 8 }}>
                      {(() => {
                        // Lógica condicional basada en Number of Images > 3
                        const imagesValue = parseFloat(
                          googleProfileInfo.data?.images_info
                            ?.totalMediaItemCount
                        );
                        const isImagesGood = imagesValue >= 3;

                        const mockStatus = isImagesGood ? 'good' : 'bad';
                        const mockMessage = isImagesGood
                          ? 'The number of images is OK.'
                          : 'Number Of Images should be greater than 3 images per week.';
                        const mockSuggestion = isImagesGood
                          ? 'Keep adding high-quality images to maintain visual appeal.'
                          : 'Add more high-quality images to attract potential clients.';

                        return (
                          <QualityAgentNotification
                            status={mockStatus}
                            message={mockMessage}
                            onFix={
                              !isImagesGood
                                ? () =>
                                    handleFixField(
                                      'images_info.totalMediaItemCount'
                                    )
                                : undefined
                            }
                            onDismiss={() =>
                              handleDismissNotification('numberOfImages')
                            }
                            suggestion={mockSuggestion}
                            loadingFix={loadingFix.numberOfImages}
                            loadingDismiss={loadingDismiss.numberOfImages}
                          />
                        );
                      })()}
                    </div>
                  )}
                <hr
                  style={{
                    margin: '10px 0',
                    border: 'none',
                    borderTop: '1px solid #e0e0e0'
                  }}
                />
                {/* Post Frequency */}
                <InfoField
                  title={'Post Frequency'}
                  isEditing={isEditing}
                  canEdit={!GBPfields.postFrequency}
                  hasChanges={!!editedFields.postFrequency}
                  onFieldChange={(value) =>
                    handleFieldChange('postFrequency', value)
                  }>
                  {googleProfileInfo.data?.posts_info?.posts_frecuency ? (
                    googleProfileInfo.data?.posts_info?.posts_frecuency
                  ) : (
                    <i className="fas fa-minus"></i>
                  )}
                </InfoField>
                {loadingAgent && (
                  <div style={{ marginTop: 8 }}>
                    <div
                      className="d-flex align-items-center"
                      style={{ minHeight: 60 }}>
                      <div
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-label="Loading agent"
                      />
                      <span>Analyzing post frequency...</span>
                    </div>
                  </div>
                )}
                {showAgentNotifications &&
                  !dismissedNotifications.postFrequency && (
                    <div style={{ marginTop: 8 }}>
                      {(() => {
                        // Lógica condicional basada en Post Frequency > 3
                        const postFreqValue = parseFloat(
                          googleProfileInfo.data?.posts_info?.posts_frecuency
                        );
                        const isPostFreqGood = postFreqValue > 3;

                        const mockStatus = isPostFreqGood ? 'good' : 'bad';
                        const mockMessage = isPostFreqGood
                          ? 'The post frequency is OK.'
                          : 'Post Frequency should be greater than 3 posts per week.';
                        const mockSuggestion = isPostFreqGood
                          ? 'Keep maintaining this posting frequency to stay engaged with your audience.'
                          : 'Increase your posting frequency to keep your audience engaged.';

                        return (
                          <QualityAgentNotification
                            status={mockStatus}
                            message={mockMessage}
                            onDismiss={() =>
                              handleDismissNotification('postFrequency')
                            }
                            suggestion={mockSuggestion}
                            loadingFix={loadingFix.postFrequency}
                            loadingDismiss={loadingDismiss.postFrequency}
                          />
                        );
                      })()}
                    </div>
                  )}
                <hr
                  style={{
                    margin: '10px 0',
                    border: 'none',
                    borderTop: '1px solid #e0e0e0'
                  }}
                />
                {/* Post Keyword Optimization */}
                <InfoField
                  title={'Post Keyword Optimization'}
                  isEditing={isEditing}
                  canEdit={!GBPfields.postKeywordOptimization}
                  hasChanges={!!editedFields.postKeywordOptimization}
                  onFieldChange={(value) =>
                    handleFieldChange('postKeywordOptimization', value)
                  }>
                  {visualPostsKeywords || <i className="fas fa-minus"></i>}
                </InfoField>
                {loadingAgent && (
                  <div style={{ marginTop: 8 }}>
                    <div
                      className="d-flex align-items-center"
                      style={{ minHeight: 60 }}>
                      <div
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-label="Loading agent"
                      />
                      <span>Analyzing keyword optimization...</span>
                    </div>
                  </div>
                )}
                {showAgentNotifications &&
                  !dismissedNotifications.postKeywordOptimization && (
                    <div style={{ marginTop: 8 }}>
                      {qualityAgentData?.recomendations?.posts_info
                        ?.posts_keywords?.message &&
                        (() => {
                          // Obtener datos actuales del service area
                          const currentPostKeywordOptimization =
                            visualPostsKeywords || '';

                          // Obtener recomendación del API
                          const apiRecommendation =
                            qualityAgentData.recomendations?.posts_info
                              ?.posts_keywords?.message;

                          // Comparar si la recomendación es diferente a lo actual
                          const isRecommendationDifferent =
                            apiRecommendation &&
                            JSON.stringify(currentPostKeywordOptimization) !==
                              JSON.stringify(apiRecommendation);

                          // Determinar status basado en la comparación
                          const status = isRecommendationDifferent
                            ? 'bad'
                            : 'good';
                          const message = isRecommendationDifferent
                            ? 'There is a post keyword optimization recommendation.'
                            : 'Your post keyword optimization is good.';

                          return (
                            <QualityAgentNotification
                              status={status}
                              message={message}
                              onFix={() =>
                                handleFixField('posts_info.posts_keywords')
                              }
                              onDismiss={() =>
                                handleDismissNotification(
                                  'postKeywordOptimization'
                                )
                              }
                              suggestion={apiRecommendation}
                              loadingFix={loadingFix.postKeywordOptimization}
                              loadingDismiss={
                                loadingDismiss.postKeywordOptimization
                              }
                              isAnyOperationInProgress={
                                isAnyOperationInProgress
                              }
                            />
                          );
                        })()}
                    </div>
                  )}
                {/* --- FIN CAMPOS DE CALIDAD --- */}
                <hr
                  style={{
                    margin: '10px 0',
                    border: 'none',
                    borderTop: '1px solid #e0e0e0'
                  }}
                />
                <OpeningDateField
                  title={'Opening date'}
                  isEditing={isEditing}
                  canEdit={!GBPfields.openingDate}
                  openingDate={googleProfileInfo.data?.openInfo?.openingDate}
                  onFieldChange={(value) =>
                    handleFieldChange('openingDate', value)
                  }>
                  {googleProfileInfo.data?.openInfo?.openingDate ? (
                    googleProfileInfo.data?.openInfo?.openingDate
                  ) : (
                    <i className="fas fa-minus"></i>
                  )}
                </OpeningDateField>
              </div>
              <div
                id="contact"
                className="pd-25"
                style={{ minHeight: '300px' }}>
                <div className="title">
                  <div>
                    <h5 style={{ marginBottom: '0.9rem' }}>
                      Contact Information
                    </h5>
                  </div>
                </div>
                <PhoneField
                  title={'Phone number'}
                  isEditing={isEditing}
                  canEdit={false}
                  googleProfileInfo={googleProfileInfo.data?.phoneNumbers}
                  onFieldChange={(value) =>
                    handleFieldChange('phoneNumber', value)
                  }>
                  {googleProfileInfo.data?.phoneNumbers ? (
                    <div>
                      {googleProfileInfo.data?.phoneNumbers?.primaryPhone}{' '}
                      <span className="badge badge-secondary">PRIMARY</span>
                      <br />
                      {googleProfileInfo.data?.phoneNumbers?.additionalPhones?.map(
                        (phone, index) => {
                          return (
                            <span key={index}>
                              {phone}
                              <br />
                            </span>
                          );
                        }
                      )}
                    </div>
                  ) : (
                    <i className="fas fa-minus"></i>
                  )}
                </PhoneField>
                <hr
                  style={{
                    margin: '10px 0',
                    border: 'none',
                    borderTop: '1px solid #e0e0e0'
                  }}
                />
                <InfoField
                  title={'Website'}
                  isEditing={isEditing}
                  canEdit={!GBPfields.website}
                  hasChanges={!!editedFields.website}
                  onFieldChange={(value) =>
                    handleFieldChange('website', value)
                  }>
                  {googleProfileInfo.data?.websiteUri ? (
                    googleProfileInfo.data?.websiteUri
                  ) : (
                    <i className="fas fa-minus"></i>
                  )}
                </InfoField>
                <hr
                  style={{
                    margin: '10px 0',
                    border: 'none',
                    borderTop: '1px solid #e0e0e0'
                  }}
                />
                <InfoField
                  title={
                    <>
                      Social profiles{' '}
                      <span className="badge badge-secondary">NEW</span>
                    </>
                  }
                  isEditing={isEditing}
                  canEdit={!GBPfields.socialProfiles}
                  onFieldChange={(value) =>
                    handleFieldChange('socialProfiles', value)
                  }>
                  {googleProfileInfo.data?.description ? (
                    googleProfileInfo.data?.description
                  ) : (
                    <i className="fas fa-minus"></i>
                  )}
                </InfoField>
                <hr
                  style={{
                    margin: '10px 0',
                    border: 'none',
                    borderTop: '1px solid #e0e0e0'
                  }}
                />
                <InfoField
                  title={'Short name'}
                  isEditing={isEditing}
                  canEdit={!GBPfields.shortName}
                  onFieldChange={(value) =>
                    handleFieldChange('shortName', value)
                  }>
                  {googleProfileInfo.data?.title ? (
                    googleProfileInfo.data?.title.toLowerCase()
                  ) : (
                    <i className="fas fa-minus"></i>
                  )}
                </InfoField>
              </div>

              <div id="hours" className="pd-25" style={{ minHeight: '300px' }}>
                <div className="title">
                  <div>
                    <h5 style={{ marginBottom: '0.9rem' }}>Opening hours</h5>
                  </div>
                </div>
                {googleProfileInfo.data?.regularHours?.periods && (
                  <Hours
                    title={'Hours'}
                    isEditing={isEditing}
                    canEdit={!GBPfields.hours}
                    googleProfileInfo={googleProfileInfo.data?.regularHours}
                    // googleProfileInfo={hours}
                    onFieldChange={(value) =>
                      handleFieldChange('hours', value)
                    }>
                    <style>
                      {`.opening-hours {
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                  }
                  .opening-hours span {
                    display: flex;
                    justify-content: space-between;
                    width: 100%;
                  }
                  .opening-hours .day {
                    flex: 1;
                    text-align: left;
                  }
                  .opening-hours .time {
                    flex: 1;
                    text-align: right;
                  }`}
                    </style>
                    {googleProfileInfo.data?.regularHours?.periods ? (
                      <div className="opening-hours">
                        {googleProfileInfo.data?.regularHours?.periods.map(
                          (period, index) => (
                            <span key={index}>
                              <span className="day">
                                {period.openDay.charAt(0).toUpperCase() +
                                  period.openDay.toLowerCase().slice(1)}
                              </span>
                              <span className="time">
                                {period.openTime.hours}:00 -{' '}
                                {period.closeTime.hours}:00
                              </span>
                            </span>
                          )
                        )}
                      </div>
                    ) : (
                      <i className="fas fa-minus"></i>
                    )}
                  </Hours>
                )}
                <hr
                  style={{
                    margin: '10px 0',
                    border: 'none',
                    borderTop: '1px solid #e0e0e0'
                  }}
                />
                <InfoField
                  title={'Special hours'}
                  isEditing={isEditing}
                  canEdit={!GBPfields.specialHours}
                  onFieldChange={(value) =>
                    handleFieldChange('specialHours', value)
                  }>
                  {googleProfileInfo.data?.specialHours ? (
                    <SpecialHours
                      specialHourPeriods={
                        googleProfileInfo.data.specialHours.specialHourPeriods
                      }
                    />
                  ) : (
                    <i className="fas fa-minus"></i>
                  )}
                </InfoField>
                <hr
                  style={{
                    margin: '10px 0',
                    border: 'none',
                    borderTop: '1px solid #e0e0e0'
                  }}
                />
                <InfoField
                  title={'More hours'}
                  isEditing={isEditing}
                  canEdit={!GBPfields.moreHours}
                  onFieldChange={(value) =>
                    handleFieldChange('moreHours', value)
                  }>
                  {googleProfileInfo.data?.moreHours ? (
                    googleProfileInfo.data?.moreHours
                  ) : (
                    <i className="fas fa-minus"></i>
                  )}
                </InfoField>
              </div>
              <div id="more" className="pd-25" style={{ minHeight: '300px' }}>
                <div className="title">
                  <div>
                    <h5 style={{ marginBottom: '0.9rem' }}>More</h5>
                  </div>
                </div>
                <InfoField
                  title={'From the business'}
                  isEditing={isEditing}
                  canEdit={!GBPfields.fromTheBusiness}
                  hasChanges={!!editedFields.fromTheBusiness}
                  onFieldChange={(value) =>
                    handleFieldChange('fromTheBusiness', value)
                  }>
                  {googleProfileInfo.data?.frombusiness ? (
                    googleProfileInfo.data?.frombusiness
                  ) : (
                    <i className="fas fa-minus"></i>
                  )}
                </InfoField>
                <hr
                  style={{
                    margin: '10px 0',
                    border: 'none',
                    borderTop: '1px solid #e0e0e0'
                  }}
                />
                <InfoField
                  title={'Crowd'}
                  isEditing={isEditing}
                  canEdit={!GBPfields.crowd}
                  hasChanges={!!editedFields.crowd}
                  onFieldChange={(value) => handleFieldChange('crowd', value)}>
                  {googleProfileInfo.data?.crowd ? (
                    googleProfileInfo.data?.crowd
                  ) : (
                    <i className="fas fa-minus"></i>
                  )}
                </InfoField>
                <hr
                  style={{
                    margin: '10px 0',
                    border: 'none',
                    borderTop: '1px solid #e0e0e0'
                  }}
                />
                <InfoField
                  title={'Planning'}
                  isEditing={isEditing}
                  canEdit={!GBPfields.planning}
                  hasChanges={!!editedFields.planning}
                  onFieldChange={(value) =>
                    handleFieldChange('planning', value)
                  }>
                  {googleProfileInfo.data?.planning ? (
                    googleProfileInfo.data?.planning
                  ) : (
                    <i className="fas fa-minus"></i>
                  )}
                </InfoField>
                <hr
                  style={{
                    margin: '10px 0',
                    border: 'none',
                    borderTop: '1px solid #e0e0e0'
                  }}
                />
                <InfoField
                  title={'Service options'}
                  isEditing={isEditing}
                  canEdit={!GBPfields.serviceOptions}
                  hasChanges={!!editedFields.serviceOptions}
                  onFieldChange={(value) =>
                    handleFieldChange('serviceOptions', value)
                  }>
                  {googleProfileInfo.data?.serviceOptions ? (
                    googleProfileInfo.data?.serviceOptions
                  ) : (
                    <i className="fas fa-minus"></i>
                  )}
                </InfoField>
              </div>
            </div>
            {/* Contenedor de botones fijo en la parte inferior */}
            {isEditing && (
              <div
                className="buttons-container"
                style={{
                  position: 'absolute',
                  bottom: '0',
                  left: '0',
                  right: '0',
                  backgroundColor: 'white',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderTop: '1px solid rgba(72, 94, 144, 0.16)',
                  zIndex: 10,
                  padding: '10px 20px'
                }}>
                <div className="d-flex align-items-center">
                  {Object.keys(editedFields).length > 0 && (
                    <span className="text-muted small">
                      <i className="fas fa-info-circle me-2"></i>
                      {Object.keys(editedFields).length} field(s) modified
                      {console.log('Campos modificados:', editedFields)}
                    </span>
                  )}
                </div>
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-white"
                    onClick={handleCancel}
                    disabled={Object.keys(editedFields).length === 0}>
                    Cancel
                  </button>
                  <button
                    className="btn no-hover-button"
                    onClick={handleSave}
                    disabled={Object.keys(editedFields).length === 0}>
                    {Object.keys(editedFields).length > 0
                      ? 'Save Changes'
                      : 'No Changes'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
);

GoogleBusinessProfile.propTypes = {
  isEditing: PropTypes.bool,
  setIsEditing: PropTypes.func,
  showQualityAgent: PropTypes.bool,
  setShowQualityAgent: PropTypes.func,
  handleShowQualityAgentRef: PropTypes.object,
  initialOptimizationPercentage: PropTypes.number
};

GoogleBusinessProfile.defaultProps = {
  isEditing: false,
  initialOptimizationPercentage: 0
};

export default GoogleBusinessProfile;
